// Helper function to get date for specific day starting from July 6, 2026 (skipping weekends)
const getDateOffset = (dayNumber) => {
  const startDate = new Date("2026-07-06");
  let date = new Date(startDate);
  let workingDaysAdded = 0;

  while (workingDaysAdded < dayNumber - 1) {
    date.setDate(date.getDate() + 1);
    if (date.getDay() !== 0 && date.getDay() !== 6) {
      workingDaysAdded++;
    }
  }

  return date.toISOString().split("T")[0];
};

// Helper function to get Pakistan Standard Time
const getPakistanTime = () => {
  const now = new Date();
  const utcOffset = now.getTimezoneOffset() / -60;

  if (Math.abs(utcOffset - 5) < 1) {
    return now;
  }

  return new Date(now.getTime() + 5 * 60 * 60 * 1000);
};

// Helper function to parse different date formats - UNIVERSAL DATE PARSER
const parseDate = (dateInput) => {
  if (dateInput instanceof Date) {
    return dateInput;
  }

  if (typeof dateInput === "string") {
    const cleanDate = dateInput.trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(cleanDate)) {
      const [year, month, day] = cleanDate.split("-").map(Number);
      return new Date(year, month - 1, day);
    }

    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(cleanDate)) {
      const parts = cleanDate.split("/").map(Number);
      const [first, second, year] = parts;

      if (first > 12) {
        return new Date(year, second - 1, first);
      } else if (second > 12) {
        return new Date(year, first - 1, second);
      } else {
        const testDate = new Date("2025-09-15");
        const systemFormat = testDate.toLocaleDateString();

        if (systemFormat.startsWith("9/") || systemFormat.startsWith("09/")) {
          return new Date(year, first - 1, second);
        }

        return new Date(year, second - 1, first);
      }
    }

    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(cleanDate)) {
      const [day, month, year] = cleanDate.split("-").map(Number);
      return new Date(year, month - 1, day);
    }

    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(cleanDate)) {
      const [day, month, year] = cleanDate.split(".").map(Number);
      return new Date(year, month - 1, day);
    }

    if (/^\d{1,2}\s+[a-zA-Z]{3,9}\s*,?\s*\d{4}$/.test(cleanDate)) {
      const match = cleanDate.match(/^(\d{1,2})\s+([a-zA-Z]{3,9})\s*,?\s*(\d{4})$/);
      if (match) {
        const [, day, monthName, year] = match;
        const month = parseMonthName(monthName);
        if (month !== -1) {
          return new Date(parseInt(year), month, parseInt(day));
        }
      }
    }

    if (/^[a-zA-Z]{3,9}\s+\d{1,2}\s*,?\s*\d{4}$/.test(cleanDate)) {
      const match = cleanDate.match(/^([a-zA-Z]{3,9})\s+(\d{1,2})\s*,?\s*(\d{4})$/);
      if (match) {
        const [, monthName, day, year] = match;
        const month = parseMonthName(monthName);
        if (month !== -1) {
          return new Date(parseInt(year), month, parseInt(day));
        }
      }
    }
  }

  return new Date(dateInput);
};

const parseMonthName = (monthName) => {
  const months = {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    sept: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  };

  return months[monthName.toLowerCase()] ?? -1;
};

const formatDateForConsistency = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const compareDates = (date1, date2) => {
  const parsedDate1 = parseDate(date1);
  const parsedDate2 = parseDate(date2);

  if (parsedDate1.getTime() < parsedDate2.getTime()) return -1;
  if (parsedDate1.getTime() > parsedDate2.getTime()) return 1;
  return 0;
};

export const getTodayInAllFormats = () => {
  const pakistanTime = getPakistanTime();
  return {
    iso: formatDateForConsistency(pakistanTime),
    system: pakistanTime.toLocaleDateString(),
    readable: pakistanTime.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    short: pakistanTime.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
  };
};

export const getCurrentDayNumber = () => {
  const pakistanTime = getPakistanTime();
  const currentDate = new Date(
    pakistanTime.getFullYear(),
    pakistanTime.getMonth(),
    pakistanTime.getDate()
  );

  const startDate = new Date(2026, 6, 6);
  const endDate = new Date(2026, 8, 13);

  if (currentDate < startDate) return 0;
  if (currentDate > endDate) return 41;

  if (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
    let workingDays = 0;
    let date = new Date(2026, 6, 6);

    while (date < currentDate) {
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        workingDays++;
      }
      date.setDate(date.getDate() + 1);
    }

    return Math.min(workingDays + 1, 40);
  }

  let workingDays = 0;
  let date = new Date(2026, 6, 6);

  while (date <= currentDate) {
    if (date.getDay() !== 0 && date.getDay() !== 6) {
      workingDays++;
    }
    date.setDate(date.getDate() + 1);
  }

  return Math.min(workingDays, 40);
};

const getTaskStatus = (dayNumber) => {
  const currentDay = getCurrentDayNumber();
  const pakistanTime = getPakistanTime();
  const currentDate = parseDate(formatDateForConsistency(pakistanTime));
  const isWeekendDay = currentDate.getDay() === 0 || currentDate.getDay() === 6;

  if (currentDay <= 0) {
    return "Locked";
  }

  if (dayNumber < currentDay) {
    if (dayNumber >= 31 && dayNumber <= 40) {
      return "Completed";
    }

    const daysSinceCompletion = currentDay - dayNumber;
    if (daysSinceCompletion > 3) {
      return "Expired";
    }
    return "Completed";
  }

  if (dayNumber === currentDay) {
    if (isWeekendDay) {
      return "Pending";
    }
    return "In Progress";
  }

  return "Locked";
};

export const getTaskTimer = (dayNumber) => {
  const currentDay = getCurrentDayNumber();
  const pakistanTime = getPakistanTime();
  const today = pakistanTime.toISOString().split("T")[0];
  const startDate = new Date("2026-07-06");
  const currentDate = parseDate(today);

  if (currentDate < startDate && dayNumber === 1) {
    return {
      type: "availability",
      message: "Available on Jul 6 (PKT)",
    };
  }

  if (currentDay > 0 && currentDay <= 40) {
    const deadlineDate = getDateOffset(dayNumber);
    return {
      type: "deadline",
      message: `Due ${deadlineDate}`,
    };
  }

  return null;
};

export const isWeekend = (dateString) => {
  const date = parseDate(dateString);
  const day = date.getDay();
  return day === 0 || day === 6;
};

export const getWeekendMessage = () => {
  const messages = [
    "⭐ Weekend Vibes! Time to recharge your coding superpowers!",
    "Level up your relaxation skills this weekend!",
    "Even rockets need fuel stops. Enjoy your weekend break!",
    "Weekend mode activated! Come back stronger on Monday!",
  ];

  const index = new Date().getDay() % messages.length;
  return messages[index];
};

export const trackMetadata = {
  "wordpress-dev": {
    id: "wordpress-dev",
    name: "WordPress Development",
    slug: "wordpress-development",
    description: "Learn WordPress development, themes, plugins, WooCommerce",
    duration: "8 Weeks",
    totalTasks: 40,
    color: "green",
    icon: "Globe",
  },
  "fullstack-dev": {
    id: "fullstack-dev",
    name: "Full-Stack Development",
    slug: "fullstack-development",
    description: "Master HTML, CSS, JavaScript, React, Laravel and MySQL",
    duration: "8 Weeks",
    totalTasks: 40,
    color: "indigo",
    icon: "Layers",
  },
  "data-science-ai": {
    id: "data-science-ai",
    name: "Artificial Intelligence & Machine Learning",
    slug: "data-science-ai",
    description:
      "Machine Learning, Deep Learning, Neural Networks and AI applications",
    duration: "8 Weeks",
    totalTasks: 40,
    color: "red",
    icon: "Brain",
  },
  "digital-marketing": {
    id: "digital-marketing",
    name: "Digital Marketing",
    slug: "digital-marketing",
    description: "SEO, social media, content marketing and analytics",
    duration: "8 Weeks",
    totalTasks: 40,
    color: "orange",
    icon: "Megaphone",
  },
};

// Helper function to get track by ID
export const getTrackById = (trackId) => {
  return trackMetadata[trackId] || null;
};

// Helper function to get track by name
export const getTrackByName = (trackName) => {
  if (!trackName) return null;

  const normalizedInput = trackName.toLowerCase().trim().replace(/\s+/g, ' ');

  return (
    Object.values(trackMetadata).find((track) => {
      const normalizedTrackName = track.name.toLowerCase().trim().replace(/\s+/g, ' ');
      const normalizedAlternateName = track.name
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/full-stack/g, 'full stack');

      return (
        normalizedTrackName === normalizedInput ||
        normalizedAlternateName === normalizedInput ||
        normalizedTrackName.replace(/-/g, ' ') === normalizedInput ||
        normalizedTrackName.replace(/\s+/g, '-') === normalizedInput
      );
    }) || null
  );
};

// Helper function to get all active tracks
export const getActiveTracks = () => {
  return Object.values(trackMetadata);
};

// Drive Link Submission URL
export const DRIVE_LINK_SUBMISSION_URL =
  "https://hindukushsoft.com/internship/drive-link/";

// Helper function to get drive link submission URL
export const getDriveLinkSubmissionUrl = () => {
  return DRIVE_LINK_SUBMISSION_URL;
};

// Intern Authentication Database (using track IDs)
export const internDatabase = {
  "wordpress-dev": [
    "tahakhatk87951@gmail.com",
    "abasiit693@gmail.com",
    "ahsanjunaid750@gmail.com",
  ],
  "fullstack-dev": [
    "naqeeebknn@gmail.com",
    "talhahabib842653@gmail.com",
    "ainee8117@gmail.com",
    "hamdanaly651@gmail.com",
  ],
  "data-science-ai": [
    "saramurad392@gmail.com",
    "mhujaj87@gmail.com",
    "mehreenmeraj99@gmail.com",
  ],
  "digital-marketing": [
    "mahmoodmalikkhan393@gmail.com",
    "shamsherilahi345@gmail.com",
    "irfanullah125560@gmail.com",
    "adnan.bscs2410@uoch.edu.pk",
    "wafa47414@gmail.com",
    "qureshilatif577@gmail.com",
    "mohmdshaayan@gmail.com",
  ],
};

export const vipInternEmails = [
  "attaelahi150@gmail.com",
  "mzahidiqbal129@gmail.com",
  "abuzarmishwani742@gmail.com",
  "sidrayasir575@gmail.com",
  "adilatasnim7@gmail.com",
];

const normalizeEmail = (email) => email.toLowerCase().trim();

export const isVipInternEmail = (email) => {
  if (!email) return false;

  return vipInternEmails.includes(normalizeEmail(email));
};

// Authentication helper functions
export const validateInternEmail = (email, trackName) => {
  if (!email || !trackName) return false;

  const normalizedEmail = normalizeEmail(email);

  if (isVipInternEmail(normalizedEmail)) {
    return true;
  }

  // Convert track name to track ID
  const track = getTrackByName(trackName);
  if (!track) return false;

  const trackEmails = internDatabase[track.id];
  if (!trackEmails) return false;

  return trackEmails.includes(normalizedEmail);
};

export const getInternTrack = (email) => {
  if (!email) return null;

  const normalizedEmail = email.toLowerCase().trim();

  for (const [trackId, emails] of Object.entries(internDatabase)) {
    if (emails.includes(normalizedEmail)) {
      return getTrackById(trackId);
    }
  }

  return null;
};

export const getTrackInterns = (trackName) => {
  const track = getTrackByName(trackName);
  if (!track) return [];
  return [...new Set([...(internDatabase[track.id] || []), ...vipInternEmails])];
};

export const tasksData = {
  "WordPress Development": [
    // Week 1: WordPress Setup & Basics
    {
      id: 1,
      taskType: "Task",
      date: getDateOffset(1),
      topics:
        "Introduction to WordPress, hosting, local setup (XAMPP/WAMP/Local by Flywheel)",
      task: "Install WordPress locally. Create a test site with a basic homepage and check the WordPress dashboard.",
      status: getTaskStatus(1),
      dueDate: getDateOffset(1),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/Z68IK-EoZxc?si=rB462khz0LxgW0oj\n• https://youtu.be/yyVqWUmsHgQ?si=ALP_VdmlUOADezLe\n• https://youtu.be/RtXA60HewUw?si=doT7ItBE3pIDu5dI\n\nWebsite Links:\n• https://websitelearners.com/wordpress-com-vs-wordpress-org/\n• https://websitelearners.com/how-to-install-wordpress-locally-on-your-pc-in-easy-steps/",
    },
    {
      id: 2,
      taskType: "Task",
      date: getDateOffset(2),
      topics: "WordPress Dashboard, Pages, Posts, Menus",
      task: "Create 2 pages (About, Contact) and 1 blog post. Add a navigation menu linking these pages.",
      status: getTaskStatus(2),
      dueDate: getDateOffset(2),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/lUAWMnvOYFI\n• https://youtu.be/6pUKkfdqQj4?si=NZ-CRk-rQ1zSBMXP\n• https://youtu.be/bSbRXQRNovA?si=POFH7EmOVSnFegL4\n\nWebsite Links:\n• https://www.wpbeginner.com/beginners-guide/what-is-the-difference-between-posts-vs-pages-in-wordpress/\n• https://www.wpbeginner.com/wp-themes/how-to-style-wordpress-navigation-menus/\n• https://www.wpbeginner.com/glossary/dashboard/",
    },
    {
      id: 3,
      taskType: "Task",
      date: getDateOffset(3),
      topics: "Themes, templates, theme customization",
      task: "Activate a free theme and customize it: logo, colors, fonts, and header/footer settings.",
      status: getTaskStatus(3),
      dueDate: getDateOffset(3),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/ubQ4MjA9cS8\n• https://youtu.be/OESb4D11jYU\n• https://youtu.be/0m0zx0SRmSQ?si=lB2TwyGAwMQE83C3\n\nWebsite Links:\n• https://www.wpbeginner.com/glossary/theme/\n• https://www.wpbeginner.com/beginners-guide/how-to-use-wordpress-theme-customizer/\n• https://www.wpbeginner.com/glossary/template/",
    },
    {
      id: 4,
      taskType: "Task",
      date: getDateOffset(4),
      topics: "Widgets, sidebars, plugins basics",
      task: "Add a sidebar widget, install and activate 2 essential plugins (SEO + Contact Form), and configure them.",
      status: getTaskStatus(4),
      dueDate: getDateOffset(4),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/vX3eYz0932A\n• https://youtu.be/2lYwKk9g_0c?si=n7Eei7wPn0MLrxYu\n• https://youtu.be/AyIWa3rY8HY?si=9s6DgQpqa_KV8SrY\n• https://youtu.be/YRpeN03RCOI?si=cEtxA9-ghs2dIJks\n• https://youtu.be/vX3eYz0932A\n\nWebsite Links:\n• https://www.wpbeginner.com/beginners-guide/how-to-add-and-use-widgets-in-wordpress/\n• https://www.wpbeginner.com/glossary/sidebar/\n• https://www.wpbeginner.com/wp-tutorials/how-to-create-a-wordpress-plugin/",
    },
    {
      id: 5,
      taskType: "Project",
      date: getDateOffset(5),
      topics: "Full site practice (basic)",
      task: "Create a simple 3-page website with menu, sidebar, footer, and a blog section. Add images and placeholder text.",
      status: getTaskStatus(5),
      dueDate: getDateOffset(5),
      assignedBy: "Abu Zar Mishwani",
      resources: "Resources to be added",
    },

    // Week 2: Content & Media Management (Days 6–10)
    {
      id: 6,
      taskType: "Task",
      date: getDateOffset(6),
      topics: "Media library, images, galleries",
      task: "Upload 5 images, create a gallery, and add it to a page with captions.",
      status: getTaskStatus(6),
      dueDate: getDateOffset(6),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=QI-1hv7e51Q\n• https://www.youtube.com/watch?v=YK-XO7iLyGQ\n• https://youtu.be/PcsythkdHrI?si=OajGe8-5MR593P7O\n\nWebsite Links:\n• https://www.wpbeginner.com/wp-tutorials/how-to-organize-wordpress-files-in-media-library-folders/\n• https://barn2.com/blog/wordpress-media-library-front-end/\n• https://www.wpbeginner.com/beginners-guide/how-to-create-an-image-gallery-in-wordpress/\n• https://wordpress.com/support/images/",
    },
    {
      id: 7,
      taskType: "Task",
      date: getDateOffset(7),
      topics: "Posts, categories, tags",
      task: "Create 3 blog posts. Assign categories and tags. Add featured images.",
      status: getTaskStatus(7),
      dueDate: getDateOffset(7),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/8VK6V4xh8_g?si=HTxUsmIlb3-lFzCN\n• https://youtu.be/socEDWZMc4c?si=1t46g_zfYH07elK_\n\nWebsite Links:\n• https://www.wpbeginner.com/plugins/how-to-add-categories-and-tags-for-wordpress-pages/\n• https://www.wpbeginner.com/beginners-guide/how-to-add-a-new-post-in-wordpress-and-utilize-all-the-features/",
    },
    {
      id: 8,
      taskType: "Task",
      date: getDateOffset(8),
      topics: "Pages, custom page templates",
      task: 'Create a custom "Services" page with unique layout using WordPress page editor and blocks.',
      status: getTaskStatus(8),
      dueDate: getDateOffset(8),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/DYiLkU7g7hA?si=yUC6XoIwIcJjO_af\n• https://youtu.be/bSbRXQRNovA?si=9BAtrpWbUQK3ChYG\n\nWebsite Links:\n• https://wpastra.com/guides-and-tutorials/wordpress-custom-page-template/\n• https://www.wpbeginner.com/glossary/page/",
    },
    {
      id: 9,
      taskType: "Task",
      date: getDateOffset(9),
      topics: "Menus, navigation, internal linking",
      task: "Build a footer menu and a header menu with links to all pages. Include a call-to-action button in the menu.",
      status: getTaskStatus(9),
      dueDate: getDateOffset(9),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/VlMUCxkdXc4?si=SfmhQPOii1NqPuRd\n• https://youtu.be/6_5FfqJsOD8?si=BOLarLsWSl-fh_KN\n\nWebsite Links:\n• https://www.wpbeginner.com/wp-themes/how-to-style-wordpress-navigation-menus/\n• https://www.semrush.com/blog/internal-links/",
    },
    {
      id: 10,
      taskType: "Project",
      date: getDateOffset(10),
      topics: "Content + layout practice",
      task: "Create a small business website (Home, About, Services, Blog, Contact) with images, menus, and a working contact form.",
      status: getTaskStatus(10),
      dueDate: getDateOffset(10),
      assignedBy: "Abu Zar Mishwani",
      resources: "Resources to be added",
    },

    // Week 3: WordPress Theme Development (Days 11–15)
    {
      id: 11,
      taskType: "Task",
      date: getDateOffset(11),
      topics: "HTML + CSS in WordPress, child themes",
      task: "Create a child theme of a default WordPress theme. Customize header and footer.",
      status: getTaskStatus(11),
      dueDate: getDateOffset(11),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/e_-fl5GuZc8?si=61OkU-IzyMGyPrm-\n• https://youtu.be/Wpc6FAsi7xI?si=RJAlNOoCTt0fLbHk\n• https://youtu.be/hl-M8HGFXnY?si=0rDK6BbwUOgbMBvx\n\nWebsite Links:\n• https://kinsta.com/blog/edit-wordpress-code/\n• https://www.wpbeginner.com/glossary/theme/\n• https://www.wpbeginner.com/wp-themes/how-to-create-a-wordpress-child-theme-video/",
    },
    {
      id: 12,
      taskType: "Task",
      date: getDateOffset(12),
      topics: "PHP basics in WordPress, template tags",
      task: "Edit header.php and footer.php to include site title, tagline, and dynamic menu.",
      status: getTaskStatus(12),
      dueDate: getDateOffset(12),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/mgGAvq9hmyU?si=1Ikrw-cLX3nnSmSA\n• https://youtu.be/AF0yfGg0ehs?si=cL8eltUeeCdL6_-R\n\nWebsite Links:\n• https://www.wpbeginner.com/glossary/php/\n• https://www.wpbeginner.com/glossary/template-tag/",
    },
    {
      id: 13,
      taskType: "Task",
      date: getDateOffset(13),
      topics: "Page templates and custom templates",
      task: "Create a custom template for a landing page using HTML and CSS.",
      status: getTaskStatus(13),
      dueDate: getDateOffset(13),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/DYiLkU7g7hA?si=ooCjJGQ4umUkIsVz\n• https://youtu.be/mNP1_Jg9-iw?si=6YwV48WumSBoZngZ\n\nWebsite Links:\n• https://developer.wordpress.org/themes/classic-themes/templates/page-template-files/\n• https://www.cloudways.com/blog/creating-custom-page-template-in-wordpress/",
    },
    {
      id: 14,
      taskType: "Task",
      date: getDateOffset(14),
      topics: "Loop, posts display, dynamic content",
      task: "Modify index.php to display latest 5 posts with featured images and excerpts.",
      status: getTaskStatus(14),
      dueDate: getDateOffset(14),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=SB-zz7ClleU\n• https://youtu.be/Ki5XBlid67A?si=suXREgGYLZue_YoI\n\nWebsite Links:\n• https://kinsta.com/blog/wordpress-loop/\n• https://www.wpbeginner.com/plugins/how-to-display-your-wordpress-posts-in-a-grid-layout/\n• https://www.wpbeginner.com/beginners-guide/how-to-add-dynamic-content-in-wordpress-beginners-guide/",
    },
    {
      id: 15,
      taskType: "Project",
      date: getDateOffset(15),
      topics: "Custom theme layout",
      task: "Build a custom homepage with header, banner, about section, services, and footer using your child theme.",
      status: getTaskStatus(15),
      dueDate: getDateOffset(15),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/LyFaKfrI6OU?si=GqLbBUK51gnGUvcl\n• https://youtu.be/pjbnM6KNc5g?si=WWtRz8-iyBGeZrT0\n\nWebsite Links:\n• https://www.wpbeginner.com/beginners-guide/how-to-customize-your-wordpress-theme/",
    },

    // Week 4: WordPress Plugins & Functionality (Days 16–20)
    {
      id: 16,
      taskType: "Task",
      date: getDateOffset(16),
      topics: "Plugins installation and management",
      task: "Install WooCommerce and configure basic settings (store, products, pages).",
      status: getTaskStatus(16),
      dueDate: getDateOffset(16),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/Sw2DAStN3G0?si=vBrC7qlx49b1n0_D\n• https://youtu.be/BehfMfNjiz8?si=O-rZzObUF-6YHXXh\n\nWebsite Links:\n• https://www.wpzoom.com/blog/how-to-install-wordpress-plugin/\n• https://www.wpbeginner.com/wp-tutorials/woocommerce-tutorial-ultimate-guide/",
    },
    {
      id: 17,
      taskType: "Task",
      date: getDateOffset(17),
      topics: "Custom post types",
      task: 'Create a "Portfolio" custom post type with projects, images, and categories.',
      status: getTaskStatus(17),
      dueDate: getDateOffset(17),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/vPptt9BiAKM?si=0zAdm7GR96jP90WX\n• https://youtu.be/7Wj7VWztnbU?si=ExKki95SKXsr0iU0\n\nWebsite Links:\n• https://www.wpbeginner.com/wp-tutorials/how-to-create-custom-post-types-in-wordpress/",
    },
    {
      id: 18,
      taskType: "Task",
      date: getDateOffset(18),
      topics: "Short codes & widgets",
      task: "Create a shortcode to display a custom message or featured portfolio items on pages.",
      status: getTaskStatus(18),
      dueDate: getDateOffset(18),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=sq1OZ2ZNqIw\n• https://youtu.be/o-H8G4RvNxI?si=gZBOL4DmsvRGBGgv\n\nWebsite Links:\n• https://www.wpbeginner.com/wp-tutorials/how-to-use-shortcodes-in-your-wordpress-sidebar-widgets/\n• https://www.geeksforgeeks.org/php/what-is-widgets-in-wordpress/",
    },
    {
      id: 19,
      taskType: "Task",
      date: getDateOffset(19),
      topics: "Forms & contact management",
      task: "Create a contact form with validation using Contact Form 7 or WPForms. Embed on Contact page.",
      status: getTaskStatus(19),
      dueDate: getDateOffset(19),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=MUhlw08yxcQ\n• https://youtu.be/hK1RnHrWmRM?si=-9IOg_UxnNHjXR-x\n• https://youtu.be/6aQiOX5eDxY?si=CgBjW7lAGB6Gy79T\n\nWebsite Links:\n• https://wpforms.com/\n• https://wordpress.com/plugins/browse/contact-management/",
    },
    {
      id: 20,
      taskType: "Project",
      date: getDateOffset(20),
      topics: "Plugin + feature practice",
      task: "Add testimonials and services sections using plugins or custom post types. Link all pages properly.",
      status: getTaskStatus(20),
      dueDate: getDateOffset(20),
      assignedBy: "Abu Zar Mishwani",
      resources: "Resources to be added",
    },

    // Week 5: WordPress Advanced Development (Days 21–25)
    {
      id: 21,
      taskType: "Task",
      date: getDateOffset(21),
      topics: "Advanced PHP in WordPress",
      task: "Add dynamic sidebar widgets and modify footer with copyright year using PHP.",
      status: getTaskStatus(21),
      dueDate: getDateOffset(21),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/mgGAvq9hmyU?si=jBuvKuvGVCn9EL_E\n\nWebsite Links:\n• https://www.wpbeginner.com/glossary/php/",
    },
    {
      id: 22,
      taskType: "Task",
      date: getDateOffset(22),
      topics: "Custom fields (ACF plugin)",
      task: "Add custom fields to Portfolio items (client name, project date) and display them dynamically.",
      status: getTaskStatus(22),
      dueDate: getDateOffset(22),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/BlqCTBUZiTk?si=03x54B5eF5pHE0nL\n\nWebsite Links:\n• https://kinsta.com/blog/advanced-custom-fields/",
    },
    {
      id: 23,
      taskType: "Task",
      date: getDateOffset(23),
      topics: "Menus, conditional content",
      task: "Show a special banner only on the homepage using PHP conditional tags.",
      status: getTaskStatus(23),
      dueDate: getDateOffset(23),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/lUAWMnvOYFI?si=312x2dHUsLFbFCyD\n\nWebsite Links:\n• https://wordpress.org/plugins/conditional-menus/",
    },
    {
      id: 24,
      taskType: "Task",
      date: getDateOffset(24),
      topics: "Page builders (Elementor or Gutenberg)",
      task: "Recreate homepage layout using Elementor blocks with responsive design.",
      status: getTaskStatus(24),
      dueDate: getDateOffset(24),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/v0DwunGqxSk?si=iWYxTEHB1SaX8NPP\n\nWebsite Links:\n• https://kinsta.com/blog/gutenberg-vs-elementor/",
    },
    {
      id: 25,
      taskType: "Project",
      date: getDateOffset(25),
      topics: "Advanced theme + plugins",
      task: "Build a multi-section homepage: Hero, About, Services, Portfolio, Testimonials, Blog, Contact with responsive styling.",
      status: getTaskStatus(25),
      dueDate: getDateOffset(25),
      assignedBy: "Abu Zar Mishwani",
      resources: "Resources to be added",
    },

    // Week 6: WooCommerce & E-Commerce (Days 26–30)
    {
      id: 26,
      taskType: "Task",
      date: getDateOffset(26),
      topics: "WooCommerce setup",
      task: "Install WooCommerce, add 3 products, configure basic store pages.",
      status: getTaskStatus(26),
      dueDate: getDateOffset(26),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/BehfMfNjiz8?si=fi3thBNgNZJuie-E\n• https://youtu.be/TaEbDsXuAc8?si=nD8GCR0bCZ7TZiTw\n• https://youtu.be/zR1lpFFNwOs?si=Seel7Vyg5O6U9bPc\n\nWebsite Links:\n• https://elementor.com/blog/woocommerce-tutorial/\n• https://www.wpbeginner.com/wp-tutorials/woocommerce-tutorial-ultimate-guide/",
    },
    {
      id: 27,
      taskType: "Task",
      date: getDateOffset(27),
      topics: "Product categories, attributes",
      task: "Add product categories, assign products, and add attributes (size, color, etc.).",
      status: getTaskStatus(27),
      dueDate: getDateOffset(27),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/KwatmWHjINw?si=PiR7EL2CaKK8EsMU\n• https://youtu.be/KwatmWHjINw?si=G15mg8BbYm4L5T0K\n\nWebsite Links:\n• https://www.wpbeginner.com/wp-tutorials/how-to-add-product-tags-attributes-and-categories-to-woocommerce/\n• https://woocommerce.com/document/managing-product-taxonomies/",
    },
    {
      id: 28,
      taskType: "Task",
      date: getDateOffset(28),
      topics: "Payment & shipping setup",
      task: "Enable test payment (PayPal/Stripe sandbox) and configure shipping methods.",
      status: getTaskStatus(28),
      dueDate: getDateOffset(28),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/xaaUyVaZMTA?si=y_UJBXILX8xN7_iE\n• https://youtu.be/IMZeWah7NVU?si=71CdbqxRutUGR_es\n• https://youtu.be/_df-WzfjEuU?si=IcQWfPcXbEVyygBu\n\nWebsite Links:\n• https://www.wpbeginner.com/showcase/how-to-set-up-installment-payments-in-woocommerce/comment-page-1/\n• https://woocommerce.com/posts/set-up-shipping-strategy/\n• https://themeisle.com/blog/woocommerce-shipping/",
    },
    {
      id: 29,
      taskType: "Task",
      date: getDateOffset(29),
      topics: "WooCommerce customization",
      task: "Customize product page layout using hooks or template overrides.",
      status: getTaskStatus(29),
      dueDate: getDateOffset(29),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/mOtAGIrgcLo?si=0MaXCZvZT83yYKkw\n\nWebsite Links:\n• https://wordpress.org/plugins/woocommerce-customizer/\n• https://www.wpbeginner.com/wp-tutorials/how-to-customize-woocommerce-checkout-page/\n• https://www.wpbeginner.com/wp-tutorials/how-to-customize-woocommerce-product-pages/",
    },
    {
      id: 30,
      taskType: "Project",
      date: getDateOffset(30),
      topics: "Mini e-commerce site",
      task: "Build a functional e-commerce demo site with 5 products, categories, and working cart.",
      status: getTaskStatus(30),
      dueDate: getDateOffset(30),
      assignedBy: "Abu Zar Mishwani",
      resources: "Resources to be added",
    },

    // Week 7: WordPress Security, Performance & Deployment (Days 31–35)
    {
      id: 31,
      taskType: "Task",
      date: getDateOffset(31),
      topics: "Security basics",
      task: "Install security plugin (Wordfence/Sucuri) and configure login protection.",
      status: getTaskStatus(31),
      dueDate: getDateOffset(31),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/7eZyW1WgsJ4?si=HKqwU-LnZ9zXUTll\n\nWebsite Links:\n• https://www.wpbeginner.com/opinion/wordfence-vs-sucuri-which-one-is-better-compared/\n• https://www.wpbeginner.com/plugins/how-to-install-and-setup-wordfence-security-in-wordpress/",
    },
    {
      id: 32,
      taskType: "Task",
      date: getDateOffset(32),
      topics: "Backup & restore",
      task: "Backup the website using a plugin and restore it locally.",
      status: getTaskStatus(32),
      dueDate: getDateOffset(32),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/AJI-ZMD7qWE?si=BKtn3MeOdZuVG3ff\n• https://youtu.be/g05XnnR9Pe0?si=bEOQ1Qo4cYbbt6vS\n\nWebsite Links:\n• https://www.wpbeginner.com/beginners-guide/beginners-guide-how-to-restore-wordpress-from-backup/\n• https://www.cloudways.com/blog/backup-wordpress-website/",
    },
    {
      id: 33,
      taskType: "Task",
      date: getDateOffset(33),
      topics: "Caching & performance",
      task: "Install caching plugin and optimize images for faster load.",
      status: getTaskStatus(33),
      dueDate: getDateOffset(33),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/SYizF37NgME?si=kvU1VL5xAIvzkZpS\n• https://youtu.be/7n_mKjosoOw?si=4E8eczVEx3oj5DM-\n\nWebsite Links:\n• https://www.wpbeginner.com/wordpress-performance-speed/\n• https://bytebytego.com/guides/caching-performance/",
    },
    {
      id: 34,
      taskType: "Task",
      date: getDateOffset(34),
      topics: "SEO basics",
      task: "Install Yoast SEO, configure meta tags, sitemap, and breadcrumbs.",
      status: getTaskStatus(34),
      dueDate: getDateOffset(34),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=k99b0Q2lobU\n\nWebsite Links:\n• https://www.wpbeginner.com/wordpress-seo/",
    },
    {
      id: 35,
      taskType: "Project",
      date: getDateOffset(35),
      topics: "Pre-deployment check",
      task: "Test responsiveness, links, forms, plugins, and page speed.",
      status: getTaskStatus(35),
      dueDate: getDateOffset(35),
      assignedBy: "Abu Zar Mishwani",
      resources: "Resources to be added",
    },

    // Week 8: Capstone Project & Final Deployment (Days 36–40)
    {
      id: 36,
      taskType: "Task",
      date: getDateOffset(36),
      topics: "Project planning",
      task: "Plan a full WordPress website (portfolio, agency, blog, or e-commerce) with pages, plugins, and layout.",
      status: getTaskStatus(36),
      dueDate: getDateOffset(36),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/xXa8d3O-NK0?si=K0oC8YQy5mCDx4sk\n\nWebsite Links:\n• https://www.wpbeginner.com/guides/",
    },
    {
      id: 37,
      taskType: "Task",
      date: getDateOffset(37),
      topics: "Development",
      task: "Build homepage, header, footer, and essential pages.",
      status: getTaskStatus(37),
      dueDate: getDateOffset(37),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/xXa8d3O-NK0?si=K0oC8YQy5mCDx4sk\n• https://youtu.be/xFu1AlBpkcw?si=AR-wkr0pi_u2XU5-\n• https://youtu.be/c8y94QCaFzk?si=r0cKbQJkGCnPo6xC\n\nWebsite Links:\n• https://elementor.com/blog/header-footer-builder/",
    },
    {
      id: 38,
      taskType: "Task",
      date: getDateOffset(38),
      topics: "Content addition",
      task: "Add posts, products, portfolio items, and images.",
      status: getTaskStatus(38),
      dueDate: getDateOffset(38),
      assignedBy: "Abu Zar Mishwani",
      resources: "Resources to be added",
    },
    {
      id: 39,
      taskType: "Task",
      date: getDateOffset(39),
      topics: "Testing & polishing",
      task: "Test all forms, links, responsiveness, and plugin functionality. Fix bugs.",
      status: getTaskStatus(39),
      dueDate: getDateOffset(39),
      assignedBy: "Abu Zar Mishwani",
      resources: "Resources to be added",
    },
    {
      id: 40,
      taskType: "Project",
      date: getDateOffset(40),
      topics: "Deployment & presentation",
      task: "Deploy website on live hosting (cPanel or Cloud hosting). Submit URL and present full project workflow.",
      status: getTaskStatus(40),
      dueDate: getDateOffset(40),
      assignedBy: "Abu Zar Mishwani",
      resources: "Resources to be added",
    },
  ],

  "Graphic Design": [
    // Week 1: Design Basics & Canva
    {
      id: 1,
      taskType: "Task",
      date: getDateOffset(1),
      topics: "What is Graphic Design? Elements & Principles",
      task: "Create 1 simple flyer using Canva shapes & text",
      status: getTaskStatus(1),
      dueDate: getDateOffset(1),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Canva Tutorial, Design Principles Guide, Color Theory Basics, Typography Fundamentals",
    },
    {
      id: 2,
      taskType: "Task",
      date: getDateOffset(2),
      topics: "Canva Workspace: Layouts, Layers, Frames, Text",
      task: "Make 2 basic quote/announcement posts",
      status: getTaskStatus(2),
      dueDate: getDateOffset(2),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Canva Interface Guide, Layout Design Principles, Text Styling Tutorial, Frame Usage Guide",
    },
    {
      id: 3,
      taskType: "Task",
      date: getDateOffset(3),
      topics: "Color Theory & Pairing",
      task: "Create 2 simple color-tip cards with text",
      status: getTaskStatus(3),
      dueDate: getDateOffset(3),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Color Theory Fundamentals, Adobe Color Wheel, Color Harmony Guide, Psychology of Colors",
    },
    {
      id: 4,
      taskType: "Task",
      date: getDateOffset(4),
      topics: "Typography & Font Pairing",
      task: "Try 2 posts using different font pairs",
      status: getTaskStatus(4),
      dueDate: getDateOffset(4),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Google Fonts Guide, Font Pairing Rules, Typography Hierarchy, Readability Guidelines",
    },
    {
      id: 5,
      taskType: "Task",
      date: getDateOffset(5),
      topics: "Visual Hierarchy & Spacing",
      task: "Redesign 1 simple design with better spacing",
      status: getTaskStatus(5),
      dueDate: getDateOffset(5),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Visual Hierarchy Principles, White Space Usage, Grid Systems, Layout Balance",
    },

    // Week 2: Social Media Graphics
    {
      id: 6,
      taskType: "Task",
      date: getDateOffset(6),
      topics: "Social Media Sizes & Grids",
      task: "Create 2 Instagram-sized visuals",
      status: getTaskStatus(6),
      dueDate: getDateOffset(6),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Social Media Size Guide, Instagram Design Templates, Grid Layout Systems, Mobile-First Design",
    },
    {
      id: 7,
      taskType: "Task",
      date: getDateOffset(7),
      topics: "Text-Based Design (Quotes, CTAs)",
      task: "Make 2 quote cards with background images",
      status: getTaskStatus(7),
      dueDate: getDateOffset(7),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Quote Design Templates, CTA Design Principles, Background Image Usage, Text Overlay Techniques",
    },
    {
      id: 8,
      taskType: "Task",
      date: getDateOffset(8),
      topics: "Story Layouts (Vertical Design)",
      task: "Design 2 Instagram story templates",
      status: getTaskStatus(8),
      dueDate: getDateOffset(8),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Instagram Story Templates, Vertical Design Principles, Story Design Trends, Interactive Elements",
    },
    {
      id: 9,
      taskType: "Task",
      date: getDateOffset(9),
      topics: "Carousel Design Basics",
      task: "Create 1 simple 3-slide carousel",
      status: getTaskStatus(9),
      dueDate: getDateOffset(9),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Carousel Design Guide, Multi-slide Templates, Content Flow, Swipe Design Patterns",
    },
    {
      id: 10,
      taskType: "Task",
      date: getDateOffset(10),
      topics: "Logo Basics (Shapes + Text)",
      task: "Design a very simple logo using shapes + text",
      status: getTaskStatus(10),
      dueDate: getDateOffset(10),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Logo Design Fundamentals, Shape Psychology, Logo Typography, Brand Identity Basics",
    },

    // Week 3: Adobe Photoshop Basics
    {
      id: 11,
      taskType: "Task",
      date: getDateOffset(11),
      topics: "Photoshop Interface: Layers, Tools",
      task: "Recreate a flyer in Photoshop",
      status: getTaskStatus(11),
      dueDate: getDateOffset(11),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Photoshop Interface Guide, Layer Management, Tool Overview, Workspace Customization",
    },
    {
      id: 12,
      taskType: "Task",
      date: getDateOffset(12),
      topics: "Selection Tools (Marquee, Lasso, Magic Wand)",
      task: "Remove background from 2 images",
      status: getTaskStatus(12),
      dueDate: getDateOffset(12),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Selection Tools Tutorial, Background Removal Techniques, Refine Edge Tool, Masking Basics",
    },
    {
      id: 13,
      taskType: "Task",
      date: getDateOffset(13),
      topics: "Masking & Adjustments",
      task: "Replace background in 1 image",
      status: getTaskStatus(13),
      dueDate: getDateOffset(13),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Layer Masking Guide, Adjustment Layers, Non-destructive Editing, Blend Modes",
    },
    {
      id: 14,
      taskType: "Task",
      date: getDateOffset(14),
      topics: "Color Adjustments & Filters",
      task: "Correct colors in 2 photos",
      status: getTaskStatus(14),
      dueDate: getDateOffset(14),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Color Correction Techniques, Filter Gallery, Curves and Levels, Photo Enhancement",
    },
    {
      id: 15,
      taskType: "Task",
      date: getDateOffset(15),
      topics: "Text & Shape Tools",
      task: "Make a basic poster with text + shapes",
      status: getTaskStatus(15),
      dueDate: getDateOffset(15),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Text Tool Mastery, Shape Tools Guide, Poster Design Principles, Typography in Photoshop",
    },

    // Week 4: Advanced Photoshop Techniques
    {
      id: 16,
      taskType: "Project",
      date: getDateOffset(16),
      topics: "Product Highlighting",
      task: "Make 1 simple product showcase",
      status: getTaskStatus(16),
      dueDate: getDateOffset(16),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Product Photography, Showcase Design, Lighting Effects, Professional Presentation",
    },
    {
      id: 17,
      taskType: "Project",
      date: getDateOffset(17),
      topics: "Sales & Promotions",
      task: "Create 1 basic discount banner",
      status: getTaskStatus(17),
      dueDate: getDateOffset(17),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Promotional Design, Sales Banner Templates, CTA Design, Urgency Creation Techniques",
    },
    {
      id: 18,
      taskType: "Project",
      date: getDateOffset(18),
      topics: "Before & After Concepts",
      task: "Design 1 simple before/after image",
      status: getTaskStatus(18),
      dueDate: getDateOffset(18),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Before/After Layout, Comparison Design, Split-screen Techniques, Visual Storytelling",
    },
    {
      id: 19,
      taskType: "Project",
      date: getDateOffset(19),
      topics: "Banner Sizes (Ad Creatives)",
      task: "Create 1 ad banner (300x250)",
      status: getTaskStatus(19),
      dueDate: getDateOffset(19),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Ad Banner Specifications, Display Advertising, Banner Design Best Practices, Call-to-Action Design",
    },
    {
      id: 20,
      taskType: "Project",
      date: getDateOffset(20),
      topics: "App Store / Play Store Screenshots",
      task: "Create 2 simple app screenshots",
      status: getTaskStatus(20),
      dueDate: getDateOffset(20),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "App Store Guidelines, Screenshot Design, Mobile UI Presentation, App Marketing Visuals",
    },

    // Week 5: Adobe Illustrator Fundamentals
    {
      id: 21,
      taskType: "Task",
      date: getDateOffset(21),
      topics: "Illustrator Workspace & Vectors",
      task: "Recreate 1 simple icon in Illustrator",
      status: getTaskStatus(21),
      dueDate: getDateOffset(21),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Illustrator Interface, Vector Graphics Basics, Icon Design Principles, Scalable Graphics",
    },
    {
      id: 22,
      taskType: "Task",
      date: getDateOffset(22),
      topics: "Pen Tool Basics",
      task: "Draw 3 simple custom shapes",
      status: getTaskStatus(22),
      dueDate: getDateOffset(22),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Pen Tool Mastery, Bezier Curves, Path Creation, Custom Shape Design",
    },
    {
      id: 23,
      taskType: "Task",
      date: getDateOffset(23),
      topics: "Shape Builder Tool",
      task: "Combine shapes into 2 icons",
      status: getTaskStatus(23),
      dueDate: getDateOffset(23),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Shape Builder Tutorial, Shape Combination, Boolean Operations, Icon Construction",
    },
    {
      id: 24,
      taskType: "Task",
      date: getDateOffset(24),
      topics: "Typography in Illustrator",
      task: "Make 1 poster with styled text",
      status: getTaskStatus(24),
      dueDate: getDateOffset(24),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Typography Tools, Text Effects, Poster Typography, Vector Text Design",
    },
    {
      id: 25,
      taskType: "Task",
      date: getDateOffset(25),
      topics: "Gradients & Color Palettes",
      task: "Redesign 1 card with gradients",
      status: getTaskStatus(25),
      dueDate: getDateOffset(25),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Gradient Tool, Color Palettes, Gradient Mesh, Modern Color Trends",
    },

    // Week 6: Vector Graphics & Logo Design
    {
      id: 26,
      taskType: "Project",
      date: getDateOffset(26),
      topics: "Logo Design Principles",
      task: "Create 1 simple logo concept",
      status: getTaskStatus(26),
      dueDate: getDateOffset(26),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Logo Design Process, Brand Identity, Vector Logos, Logo Variations",
    },
    {
      id: 27,
      taskType: "Project",
      date: getDateOffset(27),
      topics: "Business Card Layouts",
      task: "Design 1 business card",
      status: getTaskStatus(27),
      dueDate: getDateOffset(27),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Business Card Design, Print Design Guidelines, Contact Information Layout, Professional Branding",
    },
    {
      id: 28,
      taskType: "Project",
      date: getDateOffset(28),
      topics: "Letterhead Basics",
      task: "Create 1 simple letterhead",
      status: getTaskStatus(28),
      dueDate: getDateOffset(28),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Letterhead Design, Corporate Identity, Document Templates, Professional Communication",
    },
    {
      id: 29,
      taskType: "Project",
      date: getDateOffset(29),
      topics: "Infographics (Icons + Steps)",
      task: "Make 1 infographic with steps",
      status: getTaskStatus(29),
      dueDate: getDateOffset(29),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Infographic Design, Data Visualization, Step-by-step Graphics, Information Hierarchy",
    },
    {
      id: 30,
      taskType: "Project",
      date: getDateOffset(30),
      topics: "Do & Don't Visuals",
      task: "Create 1 'Do & Don't' card",
      status: getTaskStatus(30),
      dueDate: getDateOffset(30),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Comparison Design, Educational Graphics, Visual Communication, Instructional Design",
    },

    // Week 7: Brand Identity & Print Design
    {
      id: 31,
      taskType: "Project",
      date: getDateOffset(31),
      topics: "Photoshop + Illustrator Together",
      task: "Make 1 poster combining both tools",
      status: getTaskStatus(31),
      dueDate: getDateOffset(31),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Cross-Platform Workflow, File Formats, Vector + Raster Combination, Design Integration",
    },
    {
      id: 32,
      taskType: "Project",
      date: getDateOffset(32),
      topics: "Canva Templates → Photoshop",
      task: "Recreate 1 Canva post in Photoshop",
      status: getTaskStatus(32),
      dueDate: getDateOffset(32),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Design Replication, Template Recreation, Tool Comparison, Workflow Optimization",
    },
    {
      id: 33,
      taskType: "Project",
      date: getDateOffset(33),
      topics: "Social Media Pack",
      task: "Create 2 posts + 1 story as a set",
      status: getTaskStatus(33),
      dueDate: getDateOffset(33),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Content Series Design, Brand Consistency, Social Media Templates, Cohesive Visual Identity",
    },
    {
      id: 34,
      taskType: "Project",
      date: getDateOffset(34),
      topics: "Mockup Basics",
      task: "Place 1 design into a mockup",
      status: getTaskStatus(34),
      dueDate: getDateOffset(34),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Smart Objects, Mockup Usage, Design Presentation, Client Presentation Techniques",
    },
    {
      id: 35,
      taskType: "Project",
      date: getDateOffset(35),
      topics: "Visual Identity Basics",
      task: "Make 1 mini style sample (colors + fonts)",
      status: getTaskStatus(35),
      dueDate: getDateOffset(35),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Style Guides, Brand Guidelines, Color Systems, Typography Systems",
    },

    // Week 8: Portfolio & Design Projects
    {
      id: 36,
      taskType: "Project",
      date: getDateOffset(36),
      topics: "Project Planning",
      task: "Pick a fictional idea (shop, café, app, etc.)",
      status: getTaskStatus(36),
      dueDate: getDateOffset(36),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Project Planning, Creative Brief, Design Strategy, Brand Concept Development",
    },
    {
      id: 37,
      taskType: "Project",
      date: getDateOffset(37),
      topics: "Logo + Colors",
      task: "Design logo + color palette for your concept",
      status: getTaskStatus(37),
      dueDate: getDateOffset(37),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Logo Development, Color Psychology, Brand Color Systems, Logo Variations",
    },
    {
      id: 38,
      taskType: "Project",
      date: getDateOffset(38),
      topics: "Social Media Assets",
      task: "Create 2–3 posts for your brand",
      status: getTaskStatus(38),
      dueDate: getDateOffset(38),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Social Media Strategy, Content Creation, Brand Application, Platform Optimization",
    },
    {
      id: 39,
      taskType: "Project",
      date: getDateOffset(39),
      topics: "Web + Print Assets",
      task: "Design 1 web banner + 1 flyer for your brand",
      status: getTaskStatus(39),
      dueDate: getDateOffset(39),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Multi-platform Design, Print vs Digital, Asset Optimization, Brand Application",
    },
    {
      id: 40,
      taskType: "Project",
      date: getDateOffset(40),
      topics: "Portfolio & Review",
      task: "Collect all works into a portfolio presentation",
      status: getTaskStatus(40),
      dueDate: getDateOffset(40),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Portfolio Creation, Design Documentation, Project Presentation, Career Development",
    },
  ],

  "Digital Marketing": [
    // Week 1: Foundations of Digital Marketing
    {
      id: 1,
      taskType: "Task",
      date: getDateOffset(1),
      topics: "Introduction to Digital Marketing: channels, goals, KPIs",
      task: "Write a 1-page report outlining the main digital marketing channels, their pros/cons, and 3 KPIs you'd track for a small business.",
      status: getTaskStatus(1),
      dueDate: getDateOffset(1),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=introduction+to+digital+marketing+tutorial\n\nWebsite Links:\n• https://www.mygreatlearning.com/blog/introduction-to-digital-marketing/\n• https://www.wrike.com/marketing-guide/faq/what-are-kpis-in-marketing/\n• https://www.investopedia.com/terms/d/digital-marketing.asp\n• https://www.wrike.com/digital-marketing-guide/faq/what-are-digital-marketing-goals-objectives/",
    },
    {
      id: 2,
      taskType: "Task",
      date: getDateOffset(2),
      topics: "Understanding the target audience and buyer personas",
      task: "Create 2–3 buyer personas for a fictional brand, including demographics, interests, and pain points.",
      status: getTaskStatus(2),
      dueDate: getDateOffset(2),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=XgXMvdVs-sY\n\nWebsite Links:\n• https://digitalmarketinginstitute.com/blog/the-beginners-guide-to-defining-buyer-personas\n• https://trainual.com/manual/target-audience-examples",
    },
    {
      id: 3,
      taskType: "Task",
      date: getDateOffset(3),
      topics: "Content marketing fundamentals",
      task: "Draft 5 blog post ideas for a fictional business. Include titles, target audience, and purpose.",
      status: getTaskStatus(3),
      dueDate: getDateOffset(3),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=content+marketing+fundamentals+tutorial\n\nWebsite Links:\n• https://www.geeksforgeeks.org/marketing/content-marketing-types-importance-strategies/",
    },
    {
      id: 4,
      taskType: "Task",
      date: getDateOffset(4),
      topics: "Social media overview: platforms, best practices",
      task: "Set up mock social media accounts (Facebook, Instagram, LinkedIn) for a brand and write 3 sample posts.",
      status: getTaskStatus(4),
      dueDate: getDateOffset(4),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=social+media+marketing+best+practices+tutorial\n\nWebsite Links:\n• https://www.investopedia.com/terms/s/social-media-marketing-smm.asp\n• https://digitalmarketinginstitute.com/blog/a-best-practice-guide-to-social-media-marketing",
    },
    {
      id: 5,
      taskType: "Project",
      date: getDateOffset(5),
      topics: "Content planning",
      task: "Create a 1-week social media content calendar with posts, captions, and hashtags.",
      status: getTaskStatus(5),
      dueDate: getDateOffset(5),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=social+media+content+calendar+tutorial\n\nWebsite Links:\n• https://contentmarketinginstitute.com/social-media-content/how-to-build-a-winning-content-plan-for-social-media-template",
    },
    // Week 2: SEO & Website Fundamentals
    {
      id: 6,
      taskType: "Task",
      date: getDateOffset(6),
      topics: "SEO basics: keywords, on-page, off-page",
      task: "Use a keyword tool to find 10 keywords for a blog topic and write meta titles and descriptions.",
      status: getTaskStatus(6),
      dueDate: getDateOffset(6),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=HOPEF6wqzDU\n\nWebsite Links:\n• https://ahrefs.com/blog/seo-basics\n• https://digitalmarketinginstitute.com/blog/complete-guide-to-on-page-and-off-page-seo",
    },
    {
      id: 7,
      taskType: "Task",
      date: getDateOffset(7),
      topics: "Website fundamentals: CMS, WordPress basics",
      task: "Set up a simple WordPress site, install a theme, and add homepage, about, and contact pages.",
      status: getTaskStatus(7),
      dueDate: getDateOffset(7),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=wordpress+basics+tutorial+for+beginners\n\nWebsite Links:\n• https://blueskynet.ca/website-fundamentals/\n• https://www.wpbeginner.com/glossary/content-management-system-cms/\n• https://www.wpmadesimple.org/guides/wordpress-basics/",
    },
    {
      id: 8,
      taskType: "Task",
      date: getDateOffset(8),
      topics: "On-page SEO: headers, images, URLs",
      task: "Optimize 1 page of your WordPress site with headers, alt tags, and internal links.",
      status: getTaskStatus(8),
      dueDate: getDateOffset(8),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=on+page+seo+optimization+tutorial\n\nWebsite Links:\n• https://mangools.com/blog/on-page-seo/\n• https://share.google/aimode/tjDl38aKLGZLWFY4o",
    },
    {
      id: 9,
      taskType: "Task",
      date: getDateOffset(9),
      topics: "Technical SEO basics: speed, mobile-friendly, sitemap",
      task: "Check your WordPress site using Google PageSpeed Insights and fix one issue.",
      status: getTaskStatus(9),
      dueDate: getDateOffset(9),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=technical+seo+tutorial+for+beginners\n\nWebsite Links:\n• https://www.semrush.com/blog/technical-seo\n• https://mangools.com/blog/on-page-seo/\n• https://leadwebpraxis.com/technical-seo-for-better-results/",
    },
    {
      id: 10,
      taskType: "Project",
      date: getDateOffset(10),
      topics: "SEO content creation",
      task: "Write a 500-word SEO-optimized blog post with images, headings, and meta description.",
      status: getTaskStatus(10),
      dueDate: getDateOffset(10),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=seo+content+writing+tutorial\n\nWebsite Links:\n• https://yoast.com/seo-copywriting/\n• https://www.semrush.com/blog/seo-writing/",
    },

    // Week 3: Social Media Marketing & Ads
    {
      id: 11,
      taskType: "Task",
      date: getDateOffset(11),
      topics: "Facebook & Instagram Ads basics",
      task: "Create mock ad campaigns with objectives, target audience, and budget.",
      status: getTaskStatus(11),
      dueDate: getDateOffset(11),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=facebook+instagram+ads+tutorial+for+beginners\n\nWebsite Links:\n• https://neilpatel.com/what-is-facebook-advertising/\n• https://blog.hootsuite.com/instagram-ads-guide/",
    },
    {
      id: 12,
      taskType: "Task",
      date: getDateOffset(12),
      topics: "LinkedIn & Twitter marketing basics",
      task: "Draft 3 professional posts for LinkedIn that highlight a brand's services.",
      status: getTaskStatus(12),
      dueDate: getDateOffset(12),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/wgE4VmqnQ_U?si=RdEeMpU-xGxfMujf\n\nWebsite Links:\n• https://www.semrush.com/blog/linkedin-marketing/\n• https://www.upwork.com/resources/x-marketing\n• https://www.theknowledgeacademy.com/blog/twitter-marketing/",
    },
    {
      id: 13,
      taskType: "Task",
      date: getDateOffset(13),
      topics: "Content creation tools: Canva, Crello",
      task: "Design 3 engaging social media graphics for a product or service.",
      status: getTaskStatus(13),
      dueDate: getDateOffset(13),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=canva+tutorial+for+social+media+graphics\n\nWebsite Links:\n• https://digitalmarketinginstitute.com/resources/lessons/content-marketing_content-creation-tools_p64o\n• https://www.canva.com/learn/how-to-canva-beginners-guide/\n• https://share.google/aimode/O6A8lHOkq6ReGvrDg",
    },
    {
      id: 14,
      taskType: "Task",
      date: getDateOffset(14),
      topics: "Social media scheduling & automation",
      task: "Use free scheduling tools (Buffer, Later) to schedule your week's posts.",
      status: getTaskStatus(14),
      dueDate: getDateOffset(14),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=buffer+later+social+media+scheduling+tutorial\n\nWebsite Links:\n• https://dlvrit.com/what-is-social-media-scheduling/\n• https://www.webfx.com/blog/social-media/social-media-automation/",
    },
    {
      id: 15,
      taskType: "Project",
      date: getDateOffset(15),
      topics: "Social campaign creation",
      task: "Plan a 5-day campaign for a product launch with graphics, captions, hashtags, and engagement strategy.",
      status: getTaskStatus(15),
      dueDate: getDateOffset(15),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=social+media+campaign+creation+tutorial\n\nWebsite Links:\n• https://www.upwork.com/resources/how-to-create-a-social-media-campaign",
    },
    // Week 4: Email Marketing & Lead Generation
    {
      id: 16,
      taskType: "Task",
      date: getDateOffset(16),
      topics: "Email marketing basics, tools (Mailchimp, ConvertKit)",
      task: "Create a mock email list and draft 2 welcome emails for new subscribers.",
      status: getTaskStatus(16),
      dueDate: getDateOffset(16),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=mailchimp+email+marketing+tutorial+for+beginners\n\nWebsite Links:\n• https://neilpatel.com/blog/beginners-guide-email-marketing/\n• https://medium.com/@nbjoshua8/mailchimp-a-guide-to-start-email-marketing-step-by-step-in-2025-and-2026-b139740992f6\n• https://backlinko.com/hub/content/convertkit",
    },
    {
      id: 17,
      taskType: "Task",
      date: getDateOffset(17),
      topics: "Email design and content",
      task: "Design a newsletter template in Mailchimp with header, body, CTA, and footer.",
      status: getTaskStatus(17),
      dueDate: getDateOffset(17),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=mailchimp+newsletter+design+tutorial\n\nWebsite Links:\n• https://www.mailmodo.com/guides/email-design/\n• https://www.clearvoice.com/resources/what-is-email-content/",
    },
    {
      id: 18,
      taskType: "Task",
      date: getDateOffset(18),
      topics: "Lead magnets: eBooks, checklists",
      task: "Create a mock lead magnet (PDF or checklist) for your brand.",
      status: getTaskStatus(18),
      dueDate: getDateOffset(18),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=how+to+create+lead+magnet+tutorial\n\nWebsite Links:\n• https://www.semrush.com/blog/what-is-lead-magnet/\n• https://socialbee.com/glossary/ebook/\n• https://share.google/aimode/gEvKK89GytBsvQeyt",
    },
    {
      id: 19,
      taskType: "Task",
      date: getDateOffset(19),
      topics: "Landing pages & conversion optimization",
      task: "Design a landing page for your lead magnet on WordPress or Canva.",
      status: getTaskStatus(19),
      dueDate: getDateOffset(19),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=landing+page+design+tutorial\n\nWebsite Links:\n• https://unbounce.com/landing-page-articles/what-is-a-landing-page/\n• https://contentsquare.com/guides/conversion-rate-optimization/",
    },
    {
      id: 20,
      taskType: "Project",
      date: getDateOffset(20),
      topics: "Lead generation funnel",
      task: "Connect landing page + lead magnet + email sequence in a mini funnel.",
      status: getTaskStatus(20),
      dueDate: getDateOffset(20),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=lead+generation+funnel+tutorial\n\nWebsite Links:\n• https://blog.hubspot.com/marketing/marketing-funnel\n• https://optinmonster.com/lead-generation-funnel/",
    },

    // Week 5: Paid Marketing & Analytics
    {
      id: 21,
      taskType: "Task",
      date: getDateOffset(21),
      topics: "Google Ads basics: search, display, remarketing",
      task: "Set up a mock Google Ads campaign for a product or service.",
      status: getTaskStatus(21),
      dueDate: getDateOffset(21),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=google+ads+tutorial+for+beginners\n\nWebsite Links:\n• https://neilpatel.com/what-is-google-adwords/\n• https://unbounce.com/ppc/google-ads-basics/",
    },
    {
      id: 22,
      taskType: "Task",
      date: getDateOffset(22),
      topics: "Google Analytics basics",
      task: "Set up Google Analytics on your WordPress site and explore key metrics.",
      status: getTaskStatus(22),
      dueDate: getDateOffset(22),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=google+analytics+setup+tutorial\n\nWebsite Links:\n• https://support.google.com/analytics/answer/1008015\n• https://www.semrush.com/blog/google-analytics-guide/",
    },
    {
      id: 23,
      taskType: "Task",
      date: getDateOffset(23),
      topics: "Conversion tracking & KPIs",
      task: "Create goals in Google Analytics (form submissions, clicks, page views).",
      status: getTaskStatus(23),
      dueDate: getDateOffset(23),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=google+analytics+conversion+tracking+tutorial\n\nWebsite Links:\n• https://www.adroll.com/blog/conversion-tracking-a-beginners-guide\n• https://digitalmarketinginstitute.com/resources/lessons/paid-search-ppc-and-display-advertising_paid-search-kpis-and-conversion-tracking_5U2Z\n• https://www.investopedia.com/terms/k/kpi.asp",
    },
    {
      id: 24,
      taskType: "Task",
      date: getDateOffset(24),
      topics: "Facebook Pixel & tracking",
      task: "Add mock Facebook Pixel code to your WordPress site for tracking conversions.",
      status: getTaskStatus(24),
      dueDate: getDateOffset(24),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=yPwZ54Bbbhs\n\nWebsite Links:\n• https://www.simplilearn.com/what-is-facebook-pixel-article\n• https://usercentrics.com/guides/marketing-measurement/tracking-pixels/",
    },
    {
      id: 25,
      taskType: "Project",
      date: getDateOffset(25),
      topics: "Campaign performance report",
      task: "Analyze your mock campaigns and write a 1-page performance report with suggestions to improve.",
      status: getTaskStatus(25),
      dueDate: getDateOffset(25),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=marketing+campaign+performance+report+tutorial\n\nWebsite Links:\n• https://www.youtube.com/results?search_query=digital+marketing+performance+report+guide",
    },
    // Week 6: Content Marketing & Storytelling
    {
      id: 26,
      taskType: "Task",
      date: getDateOffset(26),
      topics: "Blog content strategy",
      task: "Plan a 1-month blog content calendar with topics, keywords, and publishing schedule.",
      status: getTaskStatus(26),
      dueDate: getDateOffset(26),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=blog+content+strategy+tutorial\n\nWebsite Links:\n• https://share.google/aimode/ZTs45g9xLBEIlwg6J\n• https://neilpatel.com/blog/content-strategy-a-development-guide/",
    },
    {
      id: 27,
      taskType: "Task",
      date: getDateOffset(27),
      topics: "Copywriting basics: headlines, CTAs",
      task: "Write 5 social media captions with strong CTAs for a product.",
      status: getTaskStatus(27),
      dueDate: getDateOffset(27),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=copywriting+for+social+media+tutorial\n\nWebsite Links:\n• https://neilpatel.com/blog/ultimate-guide-to-copywriting/\n• https://www.awai.com/what-is-copywriting/copywriting-basics/",
    },
    {
      id: 28,
      taskType: "Task",
      date: getDateOffset(28),
      topics: "Video content marketing",
      task: "Create a 30-second promo video using Canva or CapCut.",
      status: getTaskStatus(28),
      dueDate: getDateOffset(28),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=OWSD01PJEi0\n\nWebsite Links:\n• https://nytlicensing.com/latest/methods/what-video-content-marketing-and-why-it-important/",
    },
    {
      id: 29,
      taskType: "Task",
      date: getDateOffset(29),
      topics: "Content repurposing & distribution",
      task: "Take your Day 28 video and create 3 different format versions: vertical, square, story.",
      status: getTaskStatus(29),
      dueDate: getDateOffset(29),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=zfpQgrs9e48&t=362s\n\nWebsite Links:\n• https://backlinko.com/hub/content/repurposing\n• https://www.outbrain.com/blog/content-distribution/",
    },
    {
      id: 30,
      taskType: "Project",
      date: getDateOffset(30),
      topics: "Mini content campaign",
      task: "Run a mock 3-day campaign combining blog post, social posts, email, and video.",
      status: getTaskStatus(30),
      dueDate: getDateOffset(30),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/results?search_query=integrated+content+marketing+campaign+tutorial\n\nWebsite Links:\n• https://www.youtube.com/results?search_query=multi+channel+marketing+campaign+guide",
    },

    // Week 7: Advanced Strategies & Growth
    {
      id: 31,
      taskType: "Task",
      date: getDateOffset(31),
      topics: "Influencer & affiliate marketing basics",
      task: "Identify 5 influencers for your mock brand and draft outreach messages.",
      status: getTaskStatus(31),
      dueDate: getDateOffset(31),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://www.mckinsey.com/featured-insights/mckinsey-explainers/what-is-influencer-marketing\n• https://www.investopedia.com/terms/a/affiliate-marketing.asp\n• https://www.legacymarketing.com/influencer-marketing-vs-affiliate-marketing-which-one-is-best-for-your-business/",
    },
    {
      id: 32,
      taskType: "Task",
      date: getDateOffset(32),
      topics: "Community management & engagement",
      task: "Write 5 engagement responses for social media comments/messages.",
      status: getTaskStatus(32),
      dueDate: getDateOffset(32),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://www.aimtechnologies.co/2023/10/15/what-is-community-management-in-marketing/\n• https://socialbee.com/blog/social-media-community-management/\n• https://okendo.io/resources/blog/community-engagement-marketing/",
    },
    {
      id: 33,
      taskType: "Task",
      date: getDateOffset(33),
      topics: "Retargeting campaigns & remarketing",
      task: "Plan a mock remarketing campaign targeting users who visited your landing page.",
      status: getTaskStatus(33),
      dueDate: getDateOffset(33),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://blog.hubspot.com/marketing/retargeting-campaigns-beginner-guide\n• https://advertising.amazon.com/library/guides/remarketing\n• https://www.rtbhouse.com/blog/retargeting-vs-remarketing-are-they-really-the-same",
    },
    {
      id: 34,
      taskType: "Task",
      date: getDateOffset(34),
      topics: "Growth hacking basics",
      task: "Brainstorm 3 unconventional marketing ideas to increase brand awareness.",
      status: getTaskStatus(34),
      dueDate: getDateOffset(34),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://neilpatel.com/what-is-growth-hacking/\n• https://www.semrush.com/blog/growth-hacking/",
    },
    {
      id: 35,
      taskType: "Project",
      date: getDateOffset(35),
      topics: "Mini growth campaign",
      task: "Design a 1-week growth strategy combining paid, organic, email, and content marketing.",
      status: getTaskStatus(35),
      dueDate: getDateOffset(35),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Growth Strategy Framework, Multi-channel Campaign Planning, Marketing Integration, Performance Optimization",
    },
    // Week 8: Portfolio, Reporting & Real-World Practice
    {
      id: 36,
      taskType: "Task",
      date: getDateOffset(36),
      topics: "Portfolio creation & case studies",
      task: "Compile all your campaigns, posts, graphics, and analytics into a digital marketing portfolio.",
      status: getTaskStatus(36),
      dueDate: getDateOffset(36),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://digitalmarketinginstitute.com/blog/how-to-create-an-amazing-digital-marketing-portfolio",
    },
    {
      id: 37,
      taskType: "Task",
      date: getDateOffset(37),
      topics: "Performance reporting & analytics",
      task: "Write 3 mock reports: SEO, social media, email campaign performance.",
      status: getTaskStatus(37),
      dueDate: getDateOffset(37),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Report Writing Templates, Data Analysis, Performance Metrics, Client Communication",
    },
    {
      id: 38,
      taskType: "Task",
      date: getDateOffset(38),
      topics: "Real client simulation",
      task: "Take a mock client brief and plan complete marketing strategy: SEO, content, social, email, ads.",
      status: getTaskStatus(38),
      dueDate: getDateOffset(38),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Client Brief Analysis, Strategy Development, Marketing Proposal, Integrated Campaign Planning",
    },
    {
      id: 39,
      taskType: "Task",
      date: getDateOffset(39),
      topics: "Campaign execution",
      task: "Implement the mock client strategy with posts, ads, landing page, and emails.",
      status: getTaskStatus(39),
      dueDate: getDateOffset(39),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Campaign Execution Guide, Implementation Checklist, Quality Assurance, Launch Preparation",
    },
    {
      id: 40,
      taskType: "Project",
      date: getDateOffset(40),
      topics: "Final review & showcase",
      task: "Present your portfolio and campaign results (mock metrics included). Prepare to show clients or interviews.",
      status: getTaskStatus(40),
      dueDate: getDateOffset(40),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Portfolio Presentation Tips, Mock Interview Preparation, Campaign Showcase, Professional Presentation Skills",
    },
  ],

  "Full-Stack Development": [
    // Week 1: HTML Foundations
    {
      id: 1,
      taskType: "Task",
      date: getDateOffset(1),
      topics:
        "HTML structure, <!DOCTYPE>, <html>, <head>, <body>, headings, paragraphs",
      task: "Create an index.html file that includes your name, a short introduction, and headings using <h1> to <h3>. Structure the page properly using basic HTML boilerplate.",
      status: getTaskStatus(1),
      dueDate: getDateOffset(1),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=BGeDBfCIqas&list=PLu0W_9lII9agq5TrH9XLIKQvv0iaF2X3w&index=3\n• https://www.youtube.com/watch?v=tLBlhp0SA_0\n\nWebsite Links:\n• https://www.w3schools.com/html/html_intro.asp\n• https://www.tutorialspoint.com/html/html_basic_tags.html\n• https://www.w3schools.com/tags/ref_byfunc.asp",
    },
    {
      id: 2,
      taskType: "Task",
      date: getDateOffset(2),
      topics: "Images, links, headings, paragraphs, lists",
      task: "Create a personal profile page using HTML that includes headings, paragraphs about yourself, image(s), lists of hobbies and favorite foods, and links to your social media profiles. Use proper HTML tags like <h1>, <p>, <ul>, <img>, and <a>.",
      status: getTaskStatus(2),
      dueDate: getDateOffset(2),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=1BsVhumGlNc\n• https://www.youtube.com/watch?v=ZIofkptpXO8\n\nWebsite Links:\n• https://www.w3schools.com/html/html_images.asp\n• https://www.w3schools.com/html/html_lists.asp\n• https://www.w3schools.com/html/html_links.asp",
    },
    {
      id: 3,
      taskType: "Task",
      date: getDateOffset(3),
      topics: "Tables, forms, input fields, labels, buttons",
      task: "Build a student information page that includes a table showing subjects and marks, and a contact form with name, email, message, and submit button.",
      status: getTaskStatus(3),
      dueDate: getDateOffset(3),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=1BsVhumGlNc&t=10s\n• https://www.youtube.com/watch?v=tLBlhp0SA_0&t=49s\n\nWebsite Links:\n• https://www.w3schools.com/html/html_tables.asp\n• https://www.w3schools.com/html/html_forms.asp",
    },
    {
      id: 4,
      taskType: "Task",
      date: getDateOffset(4),
      topics:
        "Semantic HTML (<header>, <nav>, <main>, <section>, <article>, <footer>)",
      task: "Create a homepage layout using semantic HTML elements. Include a header with navigation links, a main section with content, and a footer.",
      status: getTaskStatus(4),
      dueDate: getDateOffset(4),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=fhoDRB53DwY\n\nWebsite Links:\n• https://www.w3schools.com/html/html5_semantic_elements.asp",
    },
    {
      id: 5,
      taskType: "Project",
      date: getDateOffset(5),
      topics: "Complete HTML practice",
      task: "Build a complete Resume Page using only HTML. Include About Me, Education (table), Skills (list), Projects (articles), Contact Form, and Footer.",
      status: getTaskStatus(5),
      dueDate: getDateOffset(5),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=-mWTE75dBVo\n\nWebsite Links:\n• https://projects.sparkifysolutions.com/cv-page-with-html-only/",
    },
    {
      id: 6,
      taskType: "Task",
      date: getDateOffset(6),
      topics: "CSS syntax, selectors, colors, fonts, external CSS",
      task: "Link an external CSS file to your resume page and style text, fonts, colors, and background.",
      status: getTaskStatus(6),
      dueDate: getDateOffset(6),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=1dkfuga2_Ps\n• https://www.youtube.com/watch?v=1cEG1T8beO4\n• https://www.youtube.com/watch?v=aFicd4-YTfo\n\nWebsite Links:\n• https://www.w3schools.com/html/html_css.asp\n• https://www.w3schools.com/css/css_syntax.ASP\n• https://www.w3schools.com/cssref/css_selectors.php\n• https://www.w3schools.com/cssref/pr_text_color.php\n• https://www.w3schools.com/css/css_font.asp",
    },
    {
      id: 7,
      taskType: "Task",
      date: getDateOffset(7),
      topics: "Box model (margin, padding, border, width, height)",
      task: "Improve your resume layout by adding spacing, borders, and proper alignment using margin and padding.",
      status: getTaskStatus(7),
      dueDate: getDateOffset(7),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=Xrxd6cEajhM\n\nWebsite Links:\n• https://www.w3schools.com/css/css_boxmodel.asp",
    },
    {
      id: 8,
      taskType: "Task",
      date: getDateOffset(8),
      topics: "Display property, positioning (static, relative, absolute)",
      task: "Create a layout with header, sidebar, and content area using display and positioning properties.",
      status: getTaskStatus(8),
      dueDate: getDateOffset(8),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=Xrxd6cEajhM\n\nWebsite Links:\n• https://www.w3schools.com/css/css_boxmodel.asp",
    },
    {
      id: 9,
      taskType: "Task",
      date: getDateOffset(9),
      topics: "Flexbox (display: flex, justify-content, align-items)",
      task: "Create a responsive navigation bar and a card section using Flexbox.",
      status: getTaskStatus(9),
      dueDate: getDateOffset(9),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=DWk2mndNTHY\n\nWebsite Links:\n• https://www.w3schools.com/w3css/w3css_flexbox.asp",
    },
    {
      id: 10,
      taskType: "Project",
      date: getDateOffset(10),
      topics: "CSS layout + responsive design",
      task: "Convert your resume into a fully responsive portfolio website using Flexbox and media queries.",
      status: getTaskStatus(10),
      dueDate: getDateOffset(10),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=eHye3PxH4jU\n\nWebsite Links:\n• https://www.w3schools.com/css/css_rwd_mediaqueries.asp",
    },
    {
      id: 11,
      taskType: "Task",
      date: getDateOffset(11),
      topics: "JavaScript basics, variables, data types, console.log()",
      task: "Create a script.js file and log your name, age, and country to the console.",
      status: getTaskStatus(11),
      dueDate: getDateOffset(11),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=NrhP53Divco\n\nWebsite Links:\n• https://www.w3schools.com/js/",
    },
    {
      id: 12,
      taskType: "Task",
      date: getDateOffset(12),
      topics: "Conditionals (if, else), comparison operators",
      task: "Prompt the user for age and display whether they are eligible to vote.",
      status: getTaskStatus(12),
      dueDate: getDateOffset(12),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=1R4NGtsj7hw\n• https://www.youtube.com/watch?v=lsV8JQgSW1s\n\nWebsite Links:\n• https://www.w3schools.com/js/js_conditionals.asp\n• https://www.w3schools.com/js/js_operators.asp",
    },
    {
      id: 13,
      taskType: "Task",
      date: getDateOffset(13),
      topics: "Functions, parameters, return values",
      task: "Create a function that takes a name as input and prints a greeting message on the webpage.",
      status: getTaskStatus(13),
      dueDate: getDateOffset(13),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=Jtc3j4ZNZEQ\n\nWebsite Links:\n• https://www.w3schools.com/js/js_functions.asp",
    },
    {
      id: 14,
      taskType: "Task",
      date: getDateOffset(14),
      topics: "Arrays, loops (for, while)",
      task: "Create a shopping list array and display all items using a loop.",
      status: getTaskStatus(14),
      dueDate: getDateOffset(14),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=IFZZAaiatcQ\n• https://www.youtube.com/watch?v=y32sWmu-RI4\n\nWebsite Links:\n• https://www.w3schools.com/js/js_arrays.asp\n• https://www.w3schools.com/js/js_loops.asp",
    },
    {
      id: 15,
      taskType: "Project",
      date: getDateOffset(15),
      topics: "JS fundamentals combined",
      task: "Build a small calculator app using JavaScript (HTML + CSS + JS).",
      status: getTaskStatus(15),
      dueDate: getDateOffset(15),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=TXCj39dGLlY\n\nWebsite Links:\n• https://www.tutorialspoint.com/real-time-calculator-using-html-css-and-javascript",
    },
    {
      id: 16,
      taskType: "Task",
      date: getDateOffset(16),
      topics: "DOM selectors (getElementById, querySelector)",
      task: "Select HTML elements and dynamically change text and styles using JavaScript.",
      status: getTaskStatus(16),
      dueDate: getDateOffset(16),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=_8o_BiLAgQM\n\nWebsite Links:\n• https://www.w3schools.com/js/js_htmldom_elements.asp",
    },
    {
      id: 17,
      taskType: "Task",
      date: getDateOffset(17),
      topics: "DOM events (onclick, addEventListener)",
      task: "Create buttons that change colors and text on click.",
      status: getTaskStatus(17),
      dueDate: getDateOffset(17),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=CO_DAXswOrc\n\nWebsite Links:\n• https://www.w3schools.com/js/js_htmldom_events.asp",
    },
    {
      id: 18,
      taskType: "Task",
      date: getDateOffset(18),
      topics: "Form handling, validation",
      task: "Build a form that validates empty inputs before submission.",
      status: getTaskStatus(18),
      dueDate: getDateOffset(18),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=tLBlhp0SA_0&t=150s\n• https://www.youtube.com/watch?v=WLUCzSaH5kI\n\nWebsite Links:\n• https://www.tutorialspoint.com/javascript/javascript_form_handling.htm\n• https://www.w3schools.com/js/js_validation.asp",
    },
    {
      id: 19,
      taskType: "Task",
      date: getDateOffset(19),
      topics: "LocalStorage",
      task: "Store and retrieve user data (name, theme preference) using LocalStorage.",
      status: getTaskStatus(19),
      dueDate: getDateOffset(19),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=A98SPz5XLwY\n\nWebsite Links:\n• https://www.w3schools.com/jsref/prop_win_localstorage.asp",
    },
    {
      id: 20,
      taskType: "Project",
      date: getDateOffset(20),
      topics: "DOM + JS logic",
      task: "Build a To-Do List App with add, delete, and save functionality.",
      status: getTaskStatus(20),
      dueDate: getDateOffset(20),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=ExDsHljnCyE\n\nWebsite Links:\n• https://www.w3schools.com/howto/howto_js_todolist.asp",
    },
    {
      id: 21,
      taskType: "Task",
      date: getDateOffset(21),
      topics: "React introduction, JSX, project setup",
      task: "Create a React app and render a simple heading using JSX.",
      status: getTaskStatus(21),
      dueDate: getDateOffset(21),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=nhSZ4LhIii8\n• https://www.youtube.com/watch?v=S4VH8hddg8c\n\nWebsite Links:\n• https://www.w3schools.com/react/react_intro.asp\n• https://www.w3schools.com/react/react_jsx.asp",
    },
    {
      id: 22,
      taskType: "Task",
      date: getDateOffset(22),
      topics: "Components and props",
      task: "Create reusable components like Header and UserCard and pass data using props.",
      status: getTaskStatus(22),
      dueDate: getDateOffset(22),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=S4VH8hddg8c\n\nWebsite Links:\n• https://www.w3schools.com/react/react_components.asp\n• https://www.w3schools.com/react/react_props.asp",
    },
    {
      id: 23,
      taskType: "Task",
      date: getDateOffset(23),
      topics: "useState hook, events",
      task: "Build a counter app and a theme toggle using useState.",
      status: getTaskStatus(23),
      dueDate: getDateOffset(23),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=zHoWgJD0jw4\n• https://www.youtube.com/watch?v=cXkwFjBrWfk\n\nWebsite Links:\n• https://www.w3schools.com/react/react_usestate.asp\n• https://www.w3schools.com/react/react_events.asp",
    },
    {
      id: 24,
      taskType: "Task",
      date: getDateOffset(24),
      topics: "Forms, conditional rendering",
      task: "Create a form with live preview using React state.",
      status: getTaskStatus(24),
      dueDate: getDateOffset(24),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=96DGjqlAIxs\n\nWebsite Links:\n• https://www.w3schools.com/react/react_forms.asp\n• https://www.w3schools.com/react/react_conditional_rendering.asp",
    },
    {
      id: 25,
      taskType: "Project",
      date: getDateOffset(25),
      topics: "React fundamentals combined",
      task: "Build a React Task Manager (frontend only).",
      status: getTaskStatus(25),
      dueDate: getDateOffset(25),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=Gh6d-lK4xMA\n\nWebsite Links:\n• https://react.dev/",
    },
    {
      id: 26,
      taskType: "Task",
      date: getDateOffset(26),
      topics: "PHP basics, syntax, variables, loops",
      task: "Write basic PHP scripts to process form data.",
      status: getTaskStatus(26),
      dueDate: getDateOffset(26),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=2s6mIboARCM\n• https://www.youtube.com/watch?v=ny4-hGENWVk\n\nWebsite Links:\n• https://www.w3schools.com/php/php_intro.asp",
    },
    {
      id: 27,
      taskType: "Task",
      date: getDateOffset(27),
      topics: "Laravel setup, routing, controllers",
      task: "Create a Laravel project with basic routes and controllers.",
      status: getTaskStatus(27),
      dueDate: getDateOffset(27),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=Sybx-a83QQo\n• https://www.youtube.com/watch?v=oCf9Bb-crPc&t=28s\n• https://www.youtube.com/watch?v=fI8Y4rWdpzk\n\nWebsite Links:\n• https://www.w3schools.in/laravel/installation\n• https://www.w3schools.in/laravel/routing\n• https://www.w3schools.in/laravel/controllers",
    },
    {
      id: 28,
      taskType: "Task",
      date: getDateOffset(28),
      topics: "MySQL, migrations, models",
      task: "Create a database and table using Laravel migrations.",
      status: getTaskStatus(28),
      dueDate: getDateOffset(28),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=RSM0sEP_nXc\n\nWebsite Links:\n• https://laravel.com/docs/12.x/migrations",
    },
    {
      id: 29,
      taskType: "Task",
      date: getDateOffset(29),
      topics: "CRUD operations, validation",
      task: "Build a CRUD system for managing tasks using Laravel.",
      status: getTaskStatus(29),
      dueDate: getDateOffset(29),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=lraJfCorbgo\n• https://www.youtube.com/watch?v=ByuhQncSuAQ\n\nWebsite Links:\n• https://www.geeksforgeeks.org/websites-apps/crud-full-form/\n• https://www.geeksforgeeks.org/sql/crud-operations-in-mysql/",
    },
    {
      id: 30,
      taskType: "Task",
      date: getDateOffset(30),
      topics: "Authentication basics",
      task: "Implement login and registration in Laravel.",
      status: getTaskStatus(30),
      dueDate: getDateOffset(30),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=wG5cEaGVYXs\n\nWebsite Links:\n• https://laravel.com/docs/12.x/authentication",
    },
    {
      id: 31,
      taskType: "Task",
      date: getDateOffset(31),
      topics: "REST APIs, JSON responses",
      task: "Create API endpoints in Laravel to fetch data.",
      status: getTaskStatus(31),
      dueDate: getDateOffset(31),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=arfKoJ-PK38&list=PLFsCUNFvbKLkX15K-QQ-k2PxiPM-vp8Tb\n• https://www.youtube.com/watch?v=BAHpbig3Liw\n\nWebsite Links:\n• https://www.geeksforgeeks.org/node-js/rest-api-introduction/\n• https://www.w3schools.com/whatis/whatis_json.asp",
    },
    {
      id: 32,
      taskType: "Task",
      date: getDateOffset(32),
      topics: "React + API integration",
      task: "Fetch data from Laravel API and display it in React.",
      status: getTaskStatus(32),
      dueDate: getDateOffset(32),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=JFBYU1JC_f4\n\nWebsite Links:\n• https://www.geeksforgeeks.org/reactjs/how-to-fetch-data-from-an-api-in-reactjs/",
    },
    {
      id: 33,
      taskType: "Task",
      date: getDateOffset(33),
      topics: "Full-stack CRUD",
      task: "Connect React frontend with Laravel backend for create, read, update, delete operations.",
      status: getTaskStatus(33),
      dueDate: getDateOffset(33),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=CuujUc6xGok&t=2037s\n\nWebsite Links:\n• https://laravel.com/docs/12.x/eloquent",
    },
    {
      id: 34,
      taskType: "Task",
      date: getDateOffset(34),
      topics: "Authentication flow (frontend + backend)",
      task: "Protect routes and show user-specific data.",
      status: getTaskStatus(34),
      dueDate: getDateOffset(34),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=6yXmDBr9PZE\n\nWebsite Links:\n• https://www.djamware.com/post/688066ada00aac4929293995/react-authentication-with-jwt-and-role-based-access-control\n• https://supertokens.com/docs/additional-verification/mfa/protect-routes",
    },
    {
      id: 35,
      taskType: "Task",
      date: getDateOffset(35),
      topics: "Error handling, validation",
      task: "Handle backend and frontend errors properly.",
      status: getTaskStatus(35),
      dueDate: getDateOffset(35),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=HVQFi9CglIc\n\nWebsite Links:\n• https://www.tutorialspoint.com/laravel/laravel_error_handling.htm\n• https://www.inmotionhosting.com/support/edu/laravel/form-validation-in-laravel/",
    },
    {
      id: 36,
      taskType: "Task",
      date: getDateOffset(36),
      topics: "Project planning, database design",
      task: "Plan capstone project features, database schema, and API routes.",
      status: getTaskStatus(36),
      dueDate: getDateOffset(36),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=k9x0Y0s8TXA\n• https://www.youtube.com/watch?v=MiAl2mQ718s\n\nWebsite Links:\n• https://www.techtarget.com/searchcio/definition/project-planning\n• https://www.geeksforgeeks.org/dbms/database-design-in-dbms/",
    },
    {
      id: 37,
      taskType: "Project",
      date: getDateOffset(37),
      topics: "Capstone development",
      task: "Develop frontend UI and backend logic.",
      status: getTaskStatus(37),
      dueDate: getDateOffset(37),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=9NEAVBOBV18\n\nWebsite Links:\n• https://www.topuniversities.com/student-info/careers-advice-articles/what-capstone-project-why-it-important",
    },
    {
      id: 38,
      taskType: "Project",
      date: getDateOffset(38),
      topics: "Capstone development",
      task: "Complete authentication, CRUD, and data flow.",
      status: getTaskStatus(38),
      dueDate: getDateOffset(38),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=LxgdcUSFUXA\n\nWebsite Links:\n• https://laravel.com/docs/12.x/",
    },
    {
      id: 39,
      taskType: "Task",
      date: getDateOffset(39),
      topics: "Testing, debugging, deployment basics",
      task: "Fix bugs and prepare project for deployment.",
      status: getTaskStatus(39),
      dueDate: getDateOffset(39),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=YaXJeUkBe4Y&t=9s\n• https://www.youtube.com/watch?v=WHmapcIjCTQ\n• https://www.youtube.com/watch?v=Y_qU7Llx_DE\n\nWebsite Links:\n• https://medium.com/qualitynexus/debug-test-deploy-the-role-of-environments-in-software-development-cycle-891d1ffae2fa",
    },
    {
      id: 40,
      taskType: "Project",
      date: getDateOffset(40),
      topics: "Presentation & evaluation",
      task: "Present full-stack project, explain code flow, and submit GitHub repository.",
      status: getTaskStatus(40),
      dueDate: getDateOffset(40),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=HeD9VL-b9PY\n\nWebsite Links:\n• https://github.com/\n• https://www.atlassian.com/git/tutorials",
    },
  ],

  "Artificial Intelligence & Machine Learning": [
    {
      id: 1,
      taskType: "Task",
      date: getDateOffset(1),
      topics: "AI, ML, DL Fundamentals, Python Setup, Jupyter Notebook",
      task: "Learn AI, Machine Learning, and Deep Learning basics. Set up Python environment and create your first Jupyter Notebook.",
      status: getTaskStatus(1),
      dueDate: getDateOffset(1),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=UdE-W30oOXo\n• https://www.youtube.com/watch?v=a4ZeMGSJVV0\n• https://www.youtube.com/watch?v=6M5VXKLf4D4\n• https://www.youtube.com/watch?v=ddGTXBhaGWA\n• https://www.youtube.com/watch?v=HW29067qVWk\n\nWebsite Links:\n• https://www.ibm.com/think/topics/artificial-intelligence\n• https://www.ibm.com/think/topics/machine-learning\n• https://www.datacamp.com/tutorial/tutorial-deep-learning-tutorial\n• https://code.visualstudio.com/docs/python/python-tutorial\n• https://www.dataquest.io/blog/jupyter-notebook-tutorial/",
    },
    {
      id: 2,
      taskType: "Task",
      date: getDateOffset(2),
      topics: "Python Basics: Variables, Data Types, Conditions, Loops",
      task: "Master Python fundamentals: variables, data types, conditional statements, and loops.",
      status: getTaskStatus(2),
      dueDate: getDateOffset(2),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=Gf9wLsCJDqc\n• https://youtu.be/k7OTRmVrw7w\n• https://youtu.be/DZwmZ8Usvnk\n• https://youtu.be/94UHCEmprCY\n\nWebsite Links:\n• https://www.w3schools.com/python/python_variables.asp\n• https://realpython.com/python-data-types/\n• https://realpython.com/python-conditional-statements/\n• https://www.w3schools.com/python/python_for_loops.asp",
    },
    {
      id: 3,
      taskType: "Task",
      date: getDateOffset(3),
      topics: "Python Data Structures: Functions, Lists, Tuples, Dictionaries",
      task: "Learn Python data structures including functions, lists, tuples, and dictionaries.",
      status: getTaskStatus(3),
      dueDate: getDateOffset(3),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/u-OmVr_fT4s\n• https://youtu.be/W8KRzm-HUcc\n• https://youtu.be/Mf7eFtbVxFM\n• https://youtu.be/daefaLgNkw0\n\nWebsite Links:\n• https://realpython.com/defining-your-own-python-function/\n• https://www.w3schools.com/python/python_lists.asp\n• https://www.programiz.com/python-programming/tuple\n• https://realpython.com/python-dicts/",
    },
    {
      id: 4,
      taskType: "Task",
      date: getDateOffset(4),
      topics: "File Handling and Basic Debugging",
      task: "Learn file handling in Python and basic debugging techniques using Python debugger (pdb).",
      status: getTaskStatus(4),
      dueDate: getDateOffset(4),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/Uh2ebFW8OYM\n• https://youtu.be/w8QHoVam1-I\n\nWebsite Links:\n• https://www.geeksforgeeks.org/python/file-handling-python/\n• https://www.geeksforgeeks.org/python/python-debugger-python-pdb/",
    },
    {
      id: 5,
      taskType: "Project",
      date: getDateOffset(5),
      topics: "Python Logic Building Project",
      task: "Build a Python project combining variables, data structures, and logic to solve a real-world problem.",
      status: getTaskStatus(5),
      dueDate: getDateOffset(5),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/gCFkkYZSxOc\n\nWebsite Links:\n• https://www.freecodecamp.org/news/how-to-think-like-a-programmer-lessons-in-problem-solving-d1d8bf1de7d2/",
    },
    {
      id: 6,
      taskType: "Task",
      date: getDateOffset(6),
      topics: "NumPy: Basics, Arrays, Operators",
      task: "Master NumPy for numerical computing: arrays, operations, and mathematical functions.",
      status: getTaskStatus(6),
      dueDate: getDateOffset(6),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/xECXZ3tyONo\n• https://youtu.be/6a39OjkCN5I\n• https://youtu.be/v5MR5JnKcZI\n\nWebsite Links:\n• https://numpy.org/doc/stable/user/absolute_beginners.html\n• https://www.w3schools.com/python/python_arrays.asp\n• https://www.w3schools.com/python/python_operators.asp",
    },
    {
      id: 7,
      taskType: "Task",
      date: getDateOffset(7),
      topics: "Pandas: Basics and DataFrames",
      task: "Learn Pandas for data manipulation: creating and working with DataFrames.",
      status: getTaskStatus(7),
      dueDate: getDateOffset(7),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=ZyhVh-qRZPA\n• https://www.youtube.com/watch?v=F6kmIpWWEdU\n\nWebsite Links:\n• https://www.datacamp.com/tutorial/pandas\n• https://www.w3schools.com/python/pandas/pandas_dataframes.asp",
    },
    {
      id: 8,
      taskType: "Task",
      date: getDateOffset(8),
      topics: "Data Cleaning",
      task: "Learn data cleaning techniques: handling missing values, duplicates, and data transformation.",
      status: getTaskStatus(8),
      dueDate: getDateOffset(8),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/KdmPHEnPJPs\n\nWebsite Links:\n• https://www.w3schools.com/python/pandas/pandas_cleaning.asp",
    },
    {
      id: 9,
      taskType: "Task",
      date: getDateOffset(9),
      topics: "Matplotlib Data Visualization",
      task: "Create data visualizations using Matplotlib: plots, charts, and custom visualizations.",
      status: getTaskStatus(9),
      dueDate: getDateOffset(9),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/DAQNHzOcO5A\n\nWebsite Links:\n• https://www.geeksforgeeks.org/python/python-introduction-matplotlib/",
    },
    {
      id: 10,
      taskType: "Project",
      date: getDateOffset(10),
      topics: "Data Analysis Workflow Project",
      task: "Complete a full data analysis workflow: load, clean, visualize, and analyze a dataset.",
      status: getTaskStatus(10),
      dueDate: getDateOffset(10),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/NRKYn42KmSc\n\nWebsite Links:\n• https://careerfoundry.com/en/blog/data-analytics/the-data-analysis-process-step-by-step/",
    },
    {
      id: 11,
      taskType: "Task",
      date: getDateOffset(11),
      topics: "Machine Learning: Introduction, Types, Pipeline",
      task: "Learn what Machine Learning is, its types (supervised, unsupervised, reinforcement), and ML pipeline workflow.",
      status: getTaskStatus(11),
      dueDate: getDateOffset(11),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/ukzFI9rgwfU\n• https://youtu.be/1FZ0A1QCMWc\n• https://youtu.be/HWWxtVL-D9k\n\nWebsite Links:\n• https://mitsloan.mit.edu/ideas-made-to-matter/machine-learning-explained\n• https://www.upgrad.com/blog/types-of-machine-learning-algorithms/\n• https://www.geeksforgeeks.org/blogs/machine-learning-pipeline/",
    },
    {
      id: 12,
      taskType: "Task",
      date: getDateOffset(12),
      topics: "Train/Test Split and Model Training",
      task: "Learn how to split data and train machine learning models properly.",
      status: getTaskStatus(12),
      dueDate: getDateOffset(12),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/SjOfbbfI2qY\n• https://youtu.be/elojMnjn4kk\n\nWebsite Links:\n• https://machinelearningmastery.com/train-test-split-for-evaluating-machine-learning-algorithms/\n• https://www.ibm.com/think/topics/model-training",
    },
    {
      id: 13,
      taskType: "Task",
      date: getDateOffset(13),
      topics: "Linear Regression",
      task: "Master linear regression: understanding, implementing, and evaluating regression models.",
      status: getTaskStatus(13),
      dueDate: getDateOffset(13),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/nwD5U2WxTdk?list=PLuhqtP7jdD8AFocJuxC6_Zz0HepAWL9cF\n\nWebsite Links:\n• https://machinelearningmastery.com/linear-regression-for-machine-learning/",
    },
    {
      id: 14,
      taskType: "Task",
      date: getDateOffset(14),
      topics: "Classification: KNN and Logistic Regression",
      task: "Learn classification algorithms: K-Nearest Neighbors (KNN) and Logistic Regression.",
      status: getTaskStatus(14),
      dueDate: getDateOffset(14),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/yIYKR4sgzI8\n\nWebsite Links:\n• https://www.artiba.org/blog/a-beginners-guide-to-classification-in-machine-learning",
    },
    {
      id: 15,
      taskType: "Project",
      date: getDateOffset(15),
      topics: "Regression and Classification Project",
      task: "Build a machine learning project using regression or classification algorithms.",
      status: getTaskStatus(15),
      dueDate: getDateOffset(15),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/EAWR1kFtEGo\n\nWebsite Links:\n• https://www.datacamp.com/blog/machine-learning-projects-for-all-levels",
    },
    {
      id: 16,
      taskType: "Task",
      date: getDateOffset(16),
      topics:
        "Model Evaluation Metrics: Accuracy, Precision, Recall, Confusion Matrix",
      task: "Learn model evaluation metrics to assess classification model performance.",
      status: getTaskStatus(16),
      dueDate: getDateOffset(16),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/_CGTbkHwUHQ\n• https://youtu.be/H2M3fT1njXQ\n\nWebsite Links:\n• https://www.evidentlyai.com/classification-metrics/accuracy-precision-recall\n• https://www.geeksforgeeks.org/machine-learning/confusion-matrix-machine-learning/",
    },
    {
      id: 17,
      taskType: "Task",
      date: getDateOffset(17),
      topics: "Overfitting, Underfitting, and Model Tuning",
      task: "Understand overfitting and underfitting, and learn hyperparameter tuning techniques.",
      status: getTaskStatus(17),
      dueDate: getDateOffset(17),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/o3DztvnfAJg\n• https://youtu.be/HdlDYng8g9s\n\nWebsite Links:\n• https://www.geeksforgeeks.org/machine-learning/underfitting-and-overfitting-in-machine-learning/\n• https://www.jeremyjordan.me/hyperparameter-tuning/",
    },
    {
      id: 18,
      taskType: "Task",
      date: getDateOffset(18),
      topics: "Feature Scaling and Encoding",
      task: "Learn feature scaling and categorical encoding for preprocessing data.",
      status: getTaskStatus(18),
      dueDate: getDateOffset(18),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/gb1frohFFgM\n• https://youtu.be/kGemHLOEF3w\n\nWebsite Links:\n• https://machinelearningmastery.com/feature-scaling-in-practice-what-works-and-what-doesnt/\n• https://www.geeksforgeeks.org/machine-learning/one-hot-encoding-vs-label-encoding/",
    },
    {
      id: 19,
      taskType: "Task",
      date: getDateOffset(19),
      topics: "Decision Trees and Random Forests",
      task: "Master ensemble methods: Decision Trees and Random Forest algorithms.",
      status: getTaskStatus(19),
      dueDate: getDateOffset(19),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/7YTeMIq6-9s\n• https://youtu.be/J4Wdy0Wc_xQ\n\nWebsite Links:\n• https://www.kaggle.com/code/faressayah/decision-trees-random-forest-for-beginners\n• https://www.ibm.com/think/topics/random-forest",
    },
    {
      id: 20,
      taskType: "Project",
      date: getDateOffset(20),
      topics: "Supervised Learning Project",
      task: "Build a comprehensive machine learning project with multiple algorithms and evaluation.",
      status: getTaskStatus(20),
      dueDate: getDateOffset(20),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/-dJPoLm_gtE\n\nWebsite Links:\n• https://www.datacamp.com/blog/machine-learning-projects-for-all-levels",
    },
    {
      id: 21,
      taskType: "Task",
      date: getDateOffset(21),
      topics: "Neural Networks: What They Are and How They Learn",
      task: "Understand neural networks fundamentals, activation functions, and backpropagation.",
      status: getTaskStatus(21),
      dueDate: getDateOffset(21),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/bfmFfD2RIcg\n\nWebsite Links:\n• https://www.geeksforgeeks.org/machine-learning/neural-networks-a-beginners-guide/",
    },
    {
      id: 22,
      taskType: "Task",
      date: getDateOffset(22),
      topics: "Introduction to Deep Learning Frameworks",
      task: "Learn Keras and TensorFlow for building deep learning models.",
      status: getTaskStatus(22),
      dueDate: getDateOffset(22),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/6g4O5UOH304\n\nWebsite Links:\n• https://www.activestate.com/resources/quick-reads/how-to-create-a-neural-network-in-python-with-and-without-keras/",
    },
    {
      id: 23,
      taskType: "Task",
      date: getDateOffset(23),
      topics: "Image Classification Basics",
      task: "Learn image classification using convolutional neural networks (CNN).",
      status: getTaskStatus(23),
      dueDate: getDateOffset(23),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/IIs1flEtpQk\n\nWebsite Links:\n• https://www.datacamp.com/tutorial/hugging-face-image-classification-guide",
    },
    {
      id: 24,
      taskType: "Task",
      date: getDateOffset(24),
      topics: "The Transformer Architecture",
      task: "Understand transformer architecture, attention mechanisms, and modern NLP/vision models.",
      status: getTaskStatus(24),
      dueDate: getDateOffset(24),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/SZorAJ4I-sA\n\nWebsite Links:\n• https://jalammar.github.io/illustrated-transformer/",
    },
    {
      id: 25,
      taskType: "Project",
      date: getDateOffset(25),
      topics: "Deep Learning Project",
      task: "Build a deep learning project using neural networks for image or text data.",
      status: getTaskStatus(25),
      dueDate: getDateOffset(25),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/9TWX5VzlMpk\n\nWebsite Links:\n• https://www.datacamp.com/blog/computer-vision-projects",
    },
    {
      id: 26,
      taskType: "Task",
      date: getDateOffset(26),
      topics: "Natural Language Processing",
      task: "Learn NLP: text preprocessing, tokenization, embeddings, and language models.",
      status: getTaskStatus(26),
      dueDate: getDateOffset(26),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/CMrHM8a3hqw\n\nWebsite Links:\n• https://www.datacamp.com/blog/what-is-natural-language-processing",
    },
    {
      id: 27,
      taskType: "Task",
      date: getDateOffset(27),
      topics: "Recommendation Systems",
      task: "Build recommendation engines using collaborative filtering and content-based methods.",
      status: getTaskStatus(27),
      dueDate: getDateOffset(27),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/n3RKsY2H-NE\n\nWebsite Links:\n• https://www.analyticsvidhya.com/blog/2018/06/comprehensive-guide-recommendation-engine-python/",
    },
    {
      id: 28,
      taskType: "Task",
      date: getDateOffset(28),
      topics: "AI for Prediction Systems",
      task: "Learn time-series forecasting and predictive analytics for real-world applications.",
      status: getTaskStatus(28),
      dueDate: getDateOffset(28),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/LkJpNLIaeVk\n\nWebsite Links:\n• https://www.tableau.com/analytics/time-series-forecasting",
    },
    {
      id: 29,
      taskType: "Task",
      date: getDateOffset(29),
      topics: "Model Saving and Loading",
      task: "Learn how to save, load, and serialize trained models for production use.",
      status: getTaskStatus(29),
      dueDate: getDateOffset(29),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/4E1kPNWTVcs\n\nWebsite Links:\n• https://www.geeksforgeeks.org/machine-learning/saving-a-machine-learning-model/",
    },
    {
      id: 30,
      taskType: "Project",
      date: getDateOffset(30),
      topics: "Advanced AI Project",
      task: "Build an advanced AI project combining NLP, computer vision, or recommendation systems.",
      status: getTaskStatus(30),
      dueDate: getDateOffset(30),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/EugkF1hMZ9s\n\nWebsite Links:\n• https://www.projectpro.io/article/artificial-intelligence-project-ideas/461",
    },
    {
      id: 31,
      taskType: "Task",
      date: getDateOffset(31),
      topics: "How AI Systems are Deployed",
      task: "Learn deployment strategies for AI models: containerization, cloud deployment, edge computing.",
      status: getTaskStatus(31),
      dueDate: getDateOffset(31),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/bluclMxiUkA\n\nWebsite Links:\n• https://ai.koombea.com/blog/ai-deployment",
    },
    {
      id: 32,
      taskType: "Task",
      date: getDateOffset(32),
      topics: "APIs for AI Models",
      task: "Create REST APIs to serve AI models using FastAPI or Flask.",
      status: getTaskStatus(32),
      dueDate: getDateOffset(32),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/K3r4QrtLQZE\n\nWebsite Links:\n• https://www.datacamp.com/tutorial/serving-an-llm-application-as-an-api-endpoint-using-fastapi-in-python",
    },
    {
      id: 33,
      taskType: "Task",
      date: getDateOffset(33),
      topics: "Automation with AI",
      task: "Build AI-powered automation systems for business processes and workflows.",
      status: getTaskStatus(33),
      dueDate: getDateOffset(33),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/4PZYb86j4wg\n\nWebsite Links:\n• https://www.ibm.com/think/insights/ai-automation",
    },
    {
      id: 34,
      taskType: "Task",
      date: getDateOffset(34),
      topics: "Ethics, Bias, and Risks in AI",
      task: "Understand AI ethics, bias detection, fairness, and responsible AI practices.",
      status: getTaskStatus(34),
      dueDate: getDateOffset(34),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/RfuQ5B1sjfM\n\nWebsite Links:\n• https://www.researchgate.net/publication/381584371_Ethics_Bias_and_Risks_in_AI_diagnosis",
    },
    {
      id: 35,
      taskType: "Project",
      date: getDateOffset(35),
      topics: "AI Testing and Quality Assurance Project",
      task: "Build comprehensive testing and quality assurance framework for an AI system.",
      status: getTaskStatus(35),
      dueDate: getDateOffset(35),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/EugkF1hMZ9s\n\nWebsite Links:\n• https://testgrid.io/blog/ai-testing/",
    },
    {
      id: 36,
      taskType: "Task",
      date: getDateOffset(36),
      topics: "Capstone: Project Selection and Design",
      task: "Design a comprehensive AI capstone project including scope, objectives, and architecture.",
      status: getTaskStatus(36),
      dueDate: getDateOffset(36),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/rI1FcpKi0Lc\n\nWebsite Links:\n• https://unit8.com/resources/ai-project-selection-guide/",
    },
    {
      id: 37,
      taskType: "Project",
      date: getDateOffset(37),
      topics: "Capstone: Model Development",
      task: "Develop and train models for your capstone project with proper documentation.",
      status: getTaskStatus(37),
      dueDate: getDateOffset(37),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/rysdr4khB5k\n\nWebsite Links:\n• https://www.ibm.com/think/topics/model-training",
    },
    {
      id: 38,
      taskType: "Project",
      date: getDateOffset(38),
      topics: "Capstone: Integration",
      task: "Integrate models into a complete application with UI and backend services.",
      status: getTaskStatus(38),
      dueDate: getDateOffset(38),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/GERkiopdIp8\n\nWebsite Links:\n• https://www.sciencedirect.com/topics/engineering/model-integration",
    },
    {
      id: 39,
      taskType: "Project",
      date: getDateOffset(39),
      topics: "Capstone: Testing and Improvement",
      task: "Test, debug, improve model performance, and prepare for deployment.",
      status: getTaskStatus(39),
      dueDate: getDateOffset(39),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/Q0GSZD0Na1s\n\nWebsite Links:\n• https://www.geeksforgeeks.org/software-engineering/software-engineering-software-maintenance/",
    },
    {
      id: 40,
      taskType: "Project",
      date: getDateOffset(40),
      topics: "Capstone: Final Presentation",
      task: "Present complete capstone project, demonstrate functionality, and explain AI methodology.",
      status: getTaskStatus(40),
      dueDate: getDateOffset(40),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://www.youtube.com/watch?v=presentation-guide\n\nWebsite Links:\n• https://github.com/\n• https://www.geeksforgeeks.org/machine-learning/",
    },
  ],

  "Digital Literacy": [
    // Week 1: Introduction to Digital Literacy (Days 1–5)
    {
      id: 1,
      taskType: "Task",
      date: getDateOffset(1),
      topics: "Introduction to Digital Literacy, importance of computers",
      task: "Understand basic computer components (CPU, RAM, storage, input/output devices). Create a diagram labeling each part.",
      status: getTaskStatus(1),
      dueDate: getDateOffset(1),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://asanteafrica.org/what-is-digital-literacy\n• https://www.geeksforgeeks.org/computer-science-fundamentals/functional-components-of-a-computer/\n\nVideo Links:\n• https://youtu.be/xq4jHFlZ-hc?si=dW0RhHE1mqzU5YWv",
    },
    {
      id: 2,
      taskType: "Task",
      date: getDateOffset(2),
      topics: "Operating Systems (Windows/Mac/Linux) basics",
      task: "Navigate desktop, taskbar, start menu, and file explorer. Create a folder structure for practice files.",
      status: getTaskStatus(2),
      dueDate: getDateOffset(2),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://socratic.garden/lessons/5800\n\nVideo Links:\n• https://youtu.be/ACsLvXuaKxw?si=G9te7Tl0hFVuXubZ\n• https://youtu.be/kK7L2ISGucM?si=A3sGrqXJT78Iwkhf",
    },
    {
      id: 3,
      taskType: "Task",
      date: getDateOffset(3),
      topics: "Keyboard & Mouse Skills",
      task: "Practice typing using online typing tutors. Learn shortcuts: copy, paste, cut, undo, redo, save, print.",
      status: getTaskStatus(3),
      dueDate: getDateOffset(3),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://brimbanklibraries.vic.gov.au/wp-content/uploads/2024/11/Libraries-Unit-Computer-Class-Manual-Keyboard-and-Mouse-Skills.pdf\n• https://www.typingclub.com\n\nVideo Links:\n• https://youtu.be/mWcPA5_8RnM?si=gp54Ozm8FL6MTsZo",
    },
    {
      id: 4,
      taskType: "Task",
      date: getDateOffset(4),
      topics: "Basic File Management",
      task: "Create, rename, copy, move, and delete files and folders. Learn extensions (.docx, .pdf, .jpg).",
      status: getTaskStatus(4),
      dueDate: getDateOffset(4),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://w.sunybroome.edu/basic-computer-skills/functions/file_management/2definitions.html\n\nVideo Links:\n• https://youtu.be/mefdyE3rbrM?si=z86nrXIGGS2Hnl_K",
    },
    {
      id: 5,
      taskType: "Project",
      date: getDateOffset(5),
      topics: "Digital Organization",
      task: "Organize all your documents and images into proper folders. Create a small PDF report of your week's learning.",
      status: getTaskStatus(5),
      dueDate: getDateOffset(5),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://share.google/aimode/d8f3o0ztwCqwfRkA2",
    },
    // Week 2: Internet Skills & Communication (Days 6–10)
    {
      id: 6,
      taskType: "Task",
      date: getDateOffset(6),
      topics: "Internet basics, browsers, URLs",
      task: "Open multiple browsers, visit websites, and understand URLs. Bookmark 5 educational sites.",
      status: getTaskStatus(6),
      dueDate: getDateOffset(6),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://www.geeksforgeeks.org/computer-science-fundamentals/what-is-internet-definition-uses-working-advantages-and-disadvantages/\n• https://edu.gcfglobal.org/en/internetbasics/using-a-web-browser/1/",
    },
    {
      id: 7,
      taskType: "Task",
      date: getDateOffset(7),
      topics: "Search Engines & Effective Searching",
      task: 'Use Google/Bing to search for specific topics using operators (e.g., "site:edu", quotes). Save 5 useful search results.',
      status: getTaskStatus(7),
      dueDate: getDateOffset(7),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://share.google/aimode/sFqx2tVFHDkNFmkgW\n• https://www.geeksforgeeks.org/computer-science-fundamentals/what-are-search-engines-and-how-do-they-work/",
    },
    {
      id: 8,
      taskType: "Task",
      date: getDateOffset(8),
      topics: "Email Setup & Management",
      task: "Create a Gmail or Outlook account. Send, reply, and forward emails. Attach files and use CC/BCC.",
      status: getTaskStatus(8),
      dueDate: getDateOffset(8),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Video Links:\n• https://youtu.be/VO8zd3crnHM?si=4dZjpxhm_DwinZ8V",
    },
    {
      id: 9,
      taskType: "Task",
      date: getDateOffset(9),
      topics: "Online Communication Tools",
      task: "Create accounts on Zoom, Teams, or Google Meet. Join a test meeting and share screen.",
      status: getTaskStatus(9),
      dueDate: getDateOffset(9),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://share.google/aimode/iEJu0LXr2umiCNWlZ\n\nVideo Links:\n• https://youtu.be/SM_7UnjCOL8?si=kruzRRqo6EfUsI5r",
    },
    {
      id: 10,
      taskType: "Project",
      date: getDateOffset(10),
      topics: "Digital Collaboration",
      task: "Collaborate with a peer using Google Docs. Edit a shared document and track changes.",
      status: getTaskStatus(10),
      dueDate: getDateOffset(10),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://workforceexperience.hp.com/blog/what-is-digital-collaboration/",
    },
    // Week 3: Productivity Tools (Days 11–15)
    {
      id: 11,
      taskType: "Task",
      date: getDateOffset(11),
      topics: "Word Processing (MS Word/Google Docs)",
      task: "Create a document with headings, paragraphs, bullets, numbering, and images.",
      status: getTaskStatus(11),
      dueDate: getDateOffset(11),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://share.google/aimode/hGlUu3Cr7TG7Jt3Qz\n• https://study.com/academy/lesson/what-is-word-processing-software-definition-types-examples.html\n\nVideo Links:\n• https://youtu.be/AdvggUuZL34?si=lHlhIPMK0p2qOyPw",
    },
    {
      id: 12,
      taskType: "Task",
      date: getDateOffset(12),
      topics: "Spreadsheets (MS Excel/Google Sheets)",
      task: "Create a table with data, use formulas (sum, average), and make a simple chart.",
      status: getTaskStatus(12),
      dueDate: getDateOffset(12),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://www.techtarget.com/whatis/definition/spreadsheet\n\nVideo Links:\n• https://youtu.be/7Q2C9MB-YH4?si=AlTeZYeWH4BGB76J",
    },
    {
      id: 13,
      taskType: "Task",
      date: getDateOffset(13),
      topics: "Presentations (PowerPoint/Google Slides)",
      task: "Create a 5-slide presentation with images, text formatting, and transitions.",
      status: getTaskStatus(13),
      dueDate: getDateOffset(13),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://share.google/aimode/6f3tK0YquGGUoDZfV\n• https://gcgldh.org/downloads/e-Content/Learning-Material/Computer-Science/PowerPoint.pdf\n• https://share.google/aimode/TcBDnCKE467Z5X05E",
    },
    {
      id: 14,
      taskType: "Task",
      date: getDateOffset(14),
      topics: "Digital Note-Taking & PDF Tools",
      task: "Use OneNote/Notion/Google Keep to take notes. Convert a Word document to PDF.",
      status: getTaskStatus(14),
      dueDate: getDateOffset(14),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://www.notud.com/blog/digital-note-taking\n• https://www.pdf-tools.com/\n• https://share.google/aimode/69X0MINsFdSzQYdnY",
    },
    {
      id: 15,
      taskType: "Project",
      date: getDateOffset(15),
      topics: "Office Suite Integration",
      task: "Create a mini report: Excel table + chart → insert into Word → summarize in PowerPoint slides.",
      status: getTaskStatus(15),
      dueDate: getDateOffset(15),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://www.softguide.com/function/integration-of-office-software\n• https://support.microsoft.com/en-us/office/insert-and-update-excel-data-in-powerpoint-0690708a-5ce6-41b4-923f-11d57554138d",
    },
    // Week 4: Cybersecurity & Online Safety (Days 16–20)
    {
      id: 16,
      taskType: "Task",
      date: getDateOffset(16),
      topics: "Passwords & Authentication",
      task: "Create strong passwords. Enable two-factor authentication on your email or social account.",
      status: getTaskStatus(16),
      dueDate: getDateOffset(16),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://share.google/aimode/PNcp9RHwjLWLDIVKz\n• https://www.logintc.com/types-of-authentication/password-authentication/",
    },
    {
      id: 17,
      taskType: "Task",
      date: getDateOffset(17),
      topics: "Phishing, scams, malware",
      task: "Identify phishing emails and suspicious links. List steps to avoid malware.",
      status: getTaskStatus(17),
      dueDate: getDateOffset(17),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://share.google/aimode/G5c4ZRL9SGvrdJTaY",
    },
    {
      id: 18,
      taskType: "Task",
      date: getDateOffset(18),
      topics: "Secure Browsing & VPNs",
      task: "Browse in private/incognito mode. Test a VPN for safe access to blocked websites.",
      status: getTaskStatus(18),
      dueDate: getDateOffset(18),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://multilogin.com/glossary/secure-browsing\n• https://www.kaspersky.com/resource-center/definitions/what-is-a-vpn",
    },
    {
      id: 19,
      taskType: "Task",
      date: getDateOffset(19),
      topics: "Backups & Cloud Storage",
      task: "Upload 10 files to Google Drive or OneDrive. Share a folder with a peer securely.",
      status: getTaskStatus(19),
      dueDate: getDateOffset(19),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://www.geeksforgeeks.org/operating-systems/what-is-backup/\n• https://www.geeksforgeeks.org/devops/storage-systems-in-cloud/",
    },
    {
      id: 20,
      taskType: "Project",
      date: getDateOffset(20),
      topics: "Digital Safety Practice",
      task: "Create a checklist for safe online behavior: passwords, updates, phishing, secure browsing.",
      status: getTaskStatus(20),
      dueDate: getDateOffset(20),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Cybersecurity Checklist Template, Digital Safety Guide, Security Audit Tips, Personal Security Assessment",
    },
    // Week 5: Digital Collaboration & Communication Tools (Days 21–25)
    {
      id: 21,
      taskType: "Task",
      date: getDateOffset(21),
      topics: "Google Workspace Basics",
      task: "Create a shared Google Doc, edit simultaneously with a peer.",
      status: getTaskStatus(21),
      dueDate: getDateOffset(21),
      assignedBy: "Abu Zar Mishwani",
      resources: "Website Links:\n• https://workspace.google.com/training/",
    },
    {
      id: 22,
      taskType: "Task",
      date: getDateOffset(22),
      topics: "Team Communication Tools",
      task: "Set up a Slack workspace or MS Teams team. Send messages, create channels, share files.",
      status: getTaskStatus(22),
      dueDate: getDateOffset(22),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• http://launchnotes.com/glossary/team-communication-tools-in-product-management-and-operations\n• https://technologyadvice.com/blog/human-resources/internal-communication-tools/",
    },
    {
      id: 23,
      taskType: "Task",
      date: getDateOffset(23),
      topics: "Cloud Collaboration & Project Management",
      task: "Create a simple Trello or Asana board with 3 tasks. Assign tasks and track progress.",
      status: getTaskStatus(23),
      dueDate: getDateOffset(23),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://share.google/aimode/TDwE0t0tOKgGMMnJq\n• https://www.bitrix24.com/articles/what-is-cloud-collaboration-definition-benefits-and-why-it-s-essential.php\n• https://www.investopedia.com/terms/p/project-management.asp",
    },
    {
      id: 24,
      taskType: "Task",
      date: getDateOffset(24),
      topics: "Video Conferencing Etiquette",
      task: "Host a Zoom or Teams meeting, share screen, and record the session.",
      status: getTaskStatus(24),
      dueDate: getDateOffset(24),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://resources.owllabs.com/blog/video-conferencing-etiquette\n• https://thunderbird.asu.edu/thought-leadership/insights/video-conferencing-etiquette-dos-and-donts-work-video-calls",
    },
    {
      id: 25,
      taskType: "Project",
      date: getDateOffset(25),
      topics: "Digital Collaboration Project",
      task: "Complete a 1-page report collaboratively with peers using Google Docs and share the final PDF.",
      status: getTaskStatus(25),
      dueDate: getDateOffset(25),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Collaborative Writing Guide, Team Report Templates, Version Control Tips, Group Project Management",
    },
    // Week 6: Media Literacy & Content Creation (Days 26–30)
    {
      id: 26,
      taskType: "Task",
      date: getDateOffset(26),
      topics: "Image Editing Basics",
      task: "Use Canva or Photoshop to create a social media post with text and graphics.",
      status: getTaskStatus(26),
      dueDate: getDateOffset(26),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://journalism.university/electronic-media/image-editing-basics-tools-techniques-formats/",
    },
    {
      id: 27,
      taskType: "Task",
      date: getDateOffset(27),
      topics: "Video & Audio Editing Basics",
      task: "Record a 1-minute video, add text overlay and background music using free tools (CapCut, iMovie).",
      status: getTaskStatus(27),
      dueDate: getDateOffset(27),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://www.avid.com/resource-center/how-to-edit-audio\n• https://riverside.fm/blog/what-is-video-editing",
    },
    {
      id: 28,
      taskType: "Task",
      date: getDateOffset(28),
      topics: "Blogging & Content Platforms",
      task: "Create a free blog on WordPress.com or Blogger. Publish a post with images and headings.",
      status: getTaskStatus(28),
      dueDate: getDateOffset(28),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://sendpulse.com/support/glossary/blogging\n• https://www.contentful.com/content-platform/",
    },
    {
      id: 29,
      taskType: "Task",
      date: getDateOffset(29),
      topics: "Infographics & Visual Storytelling",
      task: 'Design a simple infographic summarizing "Safe Internet Practices".',
      status: getTaskStatus(29),
      dueDate: getDateOffset(29),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://www.wix.com/wixel/resources/what-is-an-infographic",
    },
    {
      id: 30,
      taskType: "Project",
      date: getDateOffset(30),
      topics: "Content Creation Project",
      task: "Create a mini digital portfolio with your blog, infographic, and video.",
      status: getTaskStatus(30),
      dueDate: getDateOffset(30),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Portfolio Website Builders, Digital Portfolio Examples, Content Organization Tips, Showcasing Work Online",
    },
    // Week 7: Data Literacy & Analysis (Days 31–35)
    {
      id: 31,
      taskType: "Task",
      date: getDateOffset(31),
      topics: "Introduction to Data & Spreadsheets",
      task: "Input sample sales or survey data in Excel/Sheets.",
      status: getTaskStatus(31),
      dueDate: getDateOffset(31),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://www.geeksforgeeks.org/data-science/what-is-data/\n\nVideo Links:\n• https://www.youtube.com/watch?v=GsHEYCqFcIk",
    },
    {
      id: 32,
      taskType: "Task",
      date: getDateOffset(32),
      topics: "Data Visualization Basics",
      task: "Create bar, pie, and line charts from your data. Customize labels and colors.",
      status: getTaskStatus(32),
      dueDate: getDateOffset(32),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://www.geeksforgeeks.org/data-visualization/data-visualization-and-its-importance/",
    },
    {
      id: 33,
      taskType: "Task",
      date: getDateOffset(33),
      topics: "Filters & Sorting",
      task: "Filter dataset by category, sort by values, and highlight top 3 items.",
      status: getTaskStatus(33),
      dueDate: getDateOffset(33),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://help.smartsheet.com/learning-track/level-2-workflows/sorting-and-filtering",
    },
    {
      id: 34,
      taskType: "Task",
      date: getDateOffset(34),
      topics: "Basic Formulas & Calculations",
      task: "Calculate sums, averages, percentages in your dataset.",
      status: getTaskStatus(34),
      dueDate: getDateOffset(34),
      assignedBy: "Abu Zar Mishwani",
      resources: "Video Links:\n• https://www.youtube.com/watch?v=ZwiQ0W5lTEg",
    },
    {
      id: 35,
      taskType: "Project",
      date: getDateOffset(35),
      topics: "Mini Data Analysis",
      task: "Prepare a 1-page report with dataset, charts, and key insights.",
      status: getTaskStatus(35),
      dueDate: getDateOffset(35),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Data Analysis Basics, Report Writing Template, Data Interpretation Guide, Analysis Presentation Tips",
    },
    // Week 8: Capstone Project & Deployment (Days 36–40)
    {
      id: 36,
      taskType: "Task",
      date: getDateOffset(36),
      topics: "Project Planning",
      task: "Plan a Digital Literacy Portfolio: Include content created, data analysis, and collaboration projects.",
      status: getTaskStatus(36),
      dueDate: getDateOffset(36),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://plane.so/blog/what-is-project-planning",
    },
    {
      id: 37,
      taskType: "Task",
      date: getDateOffset(37),
      topics: "Portfolio Development",
      task: "Organize blog posts, videos, infographics, charts in a folder or cloud document.",
      status: getTaskStatus(37),
      dueDate: getDateOffset(37),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://drexel.edu/scdc/professional-resources/application-materials/portfolio-dev",
    },
    {
      id: 38,
      taskType: "Task",
      date: getDateOffset(38),
      topics: "Polishing & Presentation",
      task: "Check formatting, captions, titles, and links. Make it visually appealing.",
      status: getTaskStatus(38),
      dueDate: getDateOffset(38),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Website Links:\n• https://share.google/aimode/XOkkkFP8VF2Wy7yVS",
    },
    {
      id: 39,
      taskType: "Task",
      date: getDateOffset(39),
      topics: "Testing & Review",
      task: "Share portfolio with a peer or mentor. Collect feedback and make improvements.",
      status: getTaskStatus(39),
      dueDate: getDateOffset(39),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Peer Review Guide, Feedback Collection Tips, Quality Assurance Checklist, Improvement Strategies",
    },
    {
      id: 40,
      taskType: "Project",
      date: getDateOffset(40),
      topics: "Final Deployment & Presentation",
      task: "Submit portfolio (PDF or cloud link). Present your 40-day digital literacy journey with highlights.",
      status: getTaskStatus(40),
      dueDate: getDateOffset(40),
      assignedBy: "Abu Zar Mishwani",
      resources:
        "Final Presentation Tips, Portfolio Submission Guide, Public Speaking Basics, Journey Documentation",
    },
  ],
};
